import React, { useState, useEffect, useCallback, useMemo, memo } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { toast } from 'sonner';
import { 
  Users, 
  Building2, 
  Calendar, 
  FolderOpen,
  Search,
  Eye,
  Edit,
  RefreshCw,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Shield,
  AlertTriangle,
  CheckCircle,
  Clock,
  Settings,
  Layers,
  Plus,
  Trash2,
  Tag,
  Briefcase,
  Star,
  Heart,
  MessageSquare,
  UserCheck,
  Lightbulb,
  HelpCircle,
  Building,
  MapPin,
  Globe,
  Target,
  Zap,
  Award,
  Rocket,
  Compass,
  Flag,
  FileQuestion,
  User,
  UsersRound,
  BookOpen,
  X,
  Cloud,
  Handshake,
  TrendingUp,
  Sparkles,
  Link,
  Activity,
  Smile,
  ThumbsUp,
  MessageCircle,
  CreditCard,
  ArrowRightLeft,
  Plane,
  HardHat,
  Factory,
  Hospital,
  GraduationCap,
  Landmark,
  Ship,
  Train,
  Truck,
  Warehouse,
  Home,
  TreePine,
  Microscope,
  Laptop,
  Mountain,
  Waves,
  Sun,
  Wind,
  Atom,
  Stethoscope,
  Gavel,
  Scale,
  Coins,
  Store,
  GripVertical,
  Pencil,
  Loader2,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Question types
const QUESTION_TYPES = [
  { value: 'free_text', label: 'Free Text' },
  { value: 'multiple_choice', label: 'Multiple Choice' },
  { value: 'dropdown', label: 'Dropdown' },
  { value: 'slider', label: 'Slider' },
  { value: 'yes_no', label: 'Yes/No' },
  { value: 'checkbox', label: 'Checkbox' },
  { value: 'multi_topic', label: 'Multi-Topic' },
];

// Available icons for segment and driver selection
const AVAILABLE_ICONS = [
  { name: 'cloud', icon: Cloud, label: 'Cloud' },
  { name: 'handshake', icon: Handshake, label: 'Handshake' },
  { name: 'zap', icon: Zap, label: 'Lightning' },
  { name: 'target', icon: Target, label: 'Target' },
  { name: 'trending-up', icon: TrendingUp, label: 'Trending Up' },
  { name: 'sparkles', icon: Sparkles, label: 'Sparkles' },
  { name: 'building', icon: Building, label: 'Building' },
  { name: 'link', icon: Link, label: 'Link' },
  { name: 'briefcase', icon: Briefcase, label: 'Briefcase' },
  { name: 'star', icon: Star, label: 'Star' },
  { name: 'users', icon: Users, label: 'Users' },
  { name: 'eye', icon: Eye, label: 'Eye' },
  { name: 'heart', icon: Heart, label: 'Heart' },
  { name: 'message-square', icon: MessageSquare, label: 'Message' },
  { name: 'user-check', icon: UserCheck, label: 'User Check' },
  { name: 'lightbulb', icon: Lightbulb, label: 'Lightbulb' },
  { name: 'help-circle', icon: HelpCircle, label: 'Question' },
  { name: 'map-pin', icon: MapPin, label: 'Location' },
  { name: 'globe', icon: Globe, label: 'Globe' },
  { name: 'award', icon: Award, label: 'Award' },
  { name: 'shield', icon: Shield, label: 'Shield' },
  { name: 'rocket', icon: Rocket, label: 'Rocket' },
  { name: 'compass', icon: Compass, label: 'Compass' },
  { name: 'flag', icon: Flag, label: 'Flag' },
  { name: 'layers', icon: Layers, label: 'Layers' },
  { name: 'tag', icon: Tag, label: 'Tag' },
  { name: 'activity', icon: Activity, label: 'Activity' },
  { name: 'smile', icon: Smile, label: 'Smile' },
  { name: 'thumbs-up', icon: ThumbsUp, label: 'Thumbs Up' },
  { name: 'message-circle', icon: MessageCircle, label: 'Chat' },
];

const getIconByName = (iconName) => {
  const iconDef = AVAILABLE_ICONS.find(i => i.name === iconName);
  return iconDef?.icon || Tag;
};

const DEMO_ICON_MAP = {
  Plane, Building2, HardHat, Factory, Hospital, GraduationCap,
  Landmark, Ship, Train, Truck, Warehouse, Home, TreePine,
  Microscope, Laptop, Globe, Rocket, Shield, Heart, Zap,
  Mountain, Waves, Sun, Wind, Atom, Briefcase, Stethoscope,
  Gavel, Scale, Coins, Store, MapPin, Target, Award, Eye,
};

const DEMO_ICON_OPTIONS = Object.keys(DEMO_ICON_MAP);

// Icon background colors for drivers (matching the screenshot)
const DRIVER_ICON_COLORS = {
  'cloud': 'bg-slate-200 text-slate-600',
  'handshake': 'bg-amber-100 text-amber-600',
  'zap': 'bg-yellow-100 text-yellow-600',
  'target': 'bg-rose-100 text-rose-600',
  'trending-up': 'bg-blue-100 text-blue-600',
  'sparkles': 'bg-orange-100 text-orange-600',
  'building': 'bg-purple-100 text-purple-600',
  'link': 'bg-pink-100 text-pink-600',
};

const getDriverIconColor = (iconName) => {
  return DRIVER_ICON_COLORS[iconName] || 'bg-slate-100 text-slate-600';
};

// Default slider configuration
const defaultSliderConfig = {
  left_label: 'Low',
  middle_label: 'Medium',
  right_label: 'High',
  min_value: 0,
  max_value: 10,
};

export default function CustomerAccountsPage() {
  const { token, setToken, setUser } = useAuth();
  const [activeTab, setActiveTab] = useState('customers');
  const [demoUsers, setDemoUsers] = useState([]);
  const [demoProjects, setDemoProjects] = useState([]);
  const [demoProjectAccess, setDemoProjectAccess] = useState([]);
  const [allProjects, setAllProjects] = useState([]);
  const [demoConfigDialogOpen, setDemoConfigDialogOpen] = useState(false);
  const [demoConfigForm, setDemoConfigForm] = useState({
    project_id: '', display_title: '', short_description: '', icon: 'Building2', sequence: 0
  });
  const [demoConfigSaving, setDemoConfigSaving] = useState(false);
  const [seedingProject, setSeedingProject] = useState(null);
  const [exportingProject, setExportingProject] = useState(null);
  
  // Customer state
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerDetails, setCustomerDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [editTrialOpen, setEditTrialOpen] = useState(false);
  const [newTrialEndDate, setNewTrialEndDate] = useState('');
  const [savingTrialDate, setSavingTrialDate] = useState(false);

  // Global Defaults state
  const [globalDefaults, setGlobalDefaults] = useState({ segment_types: [] });
  const [defaultsLoading, setDefaultsLoading] = useState(false);
  const [expandedTypes, setExpandedTypes] = useState({});
  const [typeDialogOpen, setTypeDialogOpen] = useState(false);
  const [segmentDialogOpen, setSegmentDialogOpen] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [editingSegment, setEditingSegment] = useState(null);
  const [selectedTypeForSegment, setSelectedTypeForSegment] = useState(null);
  const [typeFormData, setTypeFormData] = useState({ name: '', description: '', icon: '' });
  const [segmentFormData, setSegmentFormData] = useState({ name: '', description: '', icon: '' });
  const [saving, setSaving] = useState(false);

  // Delete confirmation state
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState({ type: null, id: null, name: '' });

  // Question Library state
  const [questionLibraryContext, setQuestionLibraryContext] = useState('about_me');
  const [aboutMeQuestions, setAboutMeQuestions] = useState([]);
  const [aboutOthersQuestions, setAboutOthersQuestions] = useState([]);
  const [publicQuestions, setPublicQuestions] = useState([]);
  const [questionsLoading, setQuestionsLoading] = useState(false);
  const [questionDialogOpen, setQuestionDialogOpen] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);

  // Driver Library state
  const [libraryDrivers, setLibraryDrivers] = useState([]);
  const [driverDialogOpen, setDriverDialogOpen] = useState(false);
  const [editingDriver, setEditingDriver] = useState(null);
  const [driverFormData, setDriverFormData] = useState({ name: '', description: '', icon: '' });
  const [driverFilter, setDriverFilter] = useState('all');

  // Mapping Category Library state
  const [mappingCategories, setMappingCategories] = useState([]);
  const [mappingCategoryDialogOpen, setMappingCategoryDialogOpen] = useState(false);
  const [editingMappingCategory, setEditingMappingCategory] = useState(null);
  const [mappingCategoryFormData, setMappingCategoryFormData] = useState({ name: '', description: '', icon: '' });

  // Admin actions state
  const [resetFreeOpen, setResetFreeOpen] = useState(false);
  const [resetFreeTarget, setResetFreeTarget] = useState(null);
  const [resetFreeMaxUsers, setResetFreeMaxUsers] = useState(100);
  const [overrideMaxOpen, setOverrideMaxOpen] = useState(false);
  const [overrideMaxTarget, setOverrideMaxTarget] = useState(null);
  const [overrideMaxUsers, setOverrideMaxUsers] = useState(100);
  const [adminActionLoading, setAdminActionLoading] = useState(false);

  // Invoice history state
  const [invoiceHistoryOpen, setInvoiceHistoryOpen] = useState(false);
  const [invoiceHistoryTarget, setInvoiceHistoryTarget] = useState(null);
  const [invoiceHistoryData, setInvoiceHistoryData] = useState(null);
  const [invoiceHistoryLoading, setInvoiceHistoryLoading] = useState(false);

  // Account Details popup (consolidated)
  const [accountDetailsOpen, setAccountDetailsOpen] = useState(false);
  const [accountDetailsTarget, setAccountDetailsTarget] = useState(null);
  const [accountDetailsLoading, setAccountDetailsLoading] = useState(false);
  const [accountType, setAccountType] = useState('user_managed');
  const [enterpriseMaxUsers, setEnterpriseMaxUsers] = useState(100);
  const [enterpriseMonthlyRevenue, setEnterpriseMonthlyRevenue] = useState('');
  const [isUnlimited, setIsUnlimited] = useState(false);
  const [trialEndDate, setTrialEndDate] = useState('');

  // Account projects & transfer state
  const [accountProjects, setAccountProjects] = useState([]);
  const [accountProjectsLoading, setAccountProjectsLoading] = useState(false);
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [transferProject, setTransferProject] = useState(null);
  const [transferTargetId, setTransferTargetId] = useState('');
  const [transferring, setTransferring] = useState(false);

  useEffect(() => {
    fetchCustomers();
  }, []);

  useEffect(() => {
    if (activeTab === 'defaults') {
      fetchGlobalDefaults();
      fetchDriverLibrary();
      fetchMappingCategories();
    }
    if (activeTab === 'questions') {
      fetchQuestionLibrary();
      fetchDriverLibrary();
    }
    if (activeTab === 'demo') {
      const hdrs = { 'Authorization': `Bearer ${token}` };
      fetch(`${API_URL}/api/admin/demo-users`, { headers: hdrs })
        .then(r => r.json()).then(data => setDemoUsers(data || [])).catch(() => {});
      fetch(`${API_URL}/api/admin/demo-projects`, { headers: hdrs })
        .then(r => r.json()).then(data => setDemoProjects(data || [])).catch(() => {});
      fetch(`${API_URL}/api/admin/demo-project-access`, { headers: hdrs })
        .then(r => r.json()).then(data => setDemoProjectAccess(data || [])).catch(() => {});
      fetch(`${API_URL}/api/admin/all-projects`, { headers: hdrs })
        .then(r => r.json()).then(data => setAllProjects(data || [])).catch(() => {});
    }
  }, [activeTab]);

  // ============== Question Library Functions ==============
  const fetchQuestionLibrary = async () => {
    setQuestionsLoading(true);
    try {
      const [aboutMeRes, aboutOthersRes, publicRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/question-library/about_me`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/admin/question-library/about_others`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/admin/question-library/public`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);
      
      if (aboutMeRes.ok) {
        const data = await aboutMeRes.json();
        setAboutMeQuestions(data.questions || []);
      }
      if (aboutOthersRes.ok) {
        const data = await aboutOthersRes.json();
        setAboutOthersQuestions(data.questions || []);
      }
      if (publicRes.ok) {
        const data = await publicRes.json();
        setPublicQuestions(data.questions || []);
      }
    } catch (error) {
      toast.error('Failed to load question library');
    } finally {
      setQuestionsLoading(false);
    }
  };

  const fetchDriverLibrary = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/driver-library`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setLibraryDrivers(data.drivers || []);
      }
    } catch (error) {
      console.error('Failed to load driver library:', error);
    }
  };

  const handleAddDriver = () => {
    setEditingDriver(null);
    setDriverFormData({ name: '', description: '', icon: '' });
    setDriverDialogOpen(true);
  };

  const handleEditDriver = (driver) => {
    setEditingDriver(driver);
    setDriverFormData({ name: driver.name, description: driver.description || '', icon: driver.icon || '' });
    setDriverDialogOpen(true);
  };

  const handleSaveDriver = async () => {
    if (!driverFormData.name.trim()) {
      toast.error('Name is required');
      return;
    }
    
    setSaving(true);
    try {
      const url = editingDriver 
        ? `${API_URL}/api/admin/driver-library/${editingDriver.id}`
        : `${API_URL}/api/admin/driver-library`;
      
      const response = await fetch(url, {
        method: editingDriver ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(driverFormData)
      });
      
      if (!response.ok) throw new Error('Failed to save driver');
      toast.success(editingDriver ? 'Driver updated' : 'Driver added');
      setDriverDialogOpen(false);
      fetchDriverLibrary();
    } catch (error) {
      toast.error('Failed to save driver');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteDriver = (driverId, driverName) => {
    setDeleteTarget({ type: 'driver', id: driverId, name: driverName });
    setDeleteConfirmOpen(true);
  };

  const handleMoveDriver = async (driverIndex, direction) => {
    const drivers = [...libraryDrivers];
    const newIndex = driverIndex + direction;
    
    // Check bounds
    if (newIndex < 0 || newIndex >= drivers.length) return;
    
    // Swap drivers
    [drivers[driverIndex], drivers[newIndex]] = [drivers[newIndex], drivers[driverIndex]];
    
    // Get new order of driver IDs
    const driverIds = drivers.map(d => d.id);
    
    try {
      const response = await fetch(
        `${API_URL}/api/admin/driver-library/reorder`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ driver_ids: driverIds })
        }
      );
      if (!response.ok) throw new Error('Failed to reorder');
      fetchDriverLibrary();
    } catch (error) {
      toast.error('Failed to reorder drivers');
    }
  };

  // ============== Mapping Category Library Functions ==============
  const fetchMappingCategories = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/mapping-categories`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setMappingCategories(data.categories || []);
      }
    } catch (error) {
      console.error('Failed to load mapping categories:', error);
    }
  };

  const handleAddMappingCategory = () => {
    setEditingMappingCategory(null);
    setMappingCategoryFormData({ name: '', description: '', icon: '' });
    setMappingCategoryDialogOpen(true);
  };

  const handleEditMappingCategory = (category) => {
    setEditingMappingCategory(category);
    setMappingCategoryFormData({ 
      name: category.name, 
      description: category.description || '', 
      icon: category.icon || '' 
    });
    setMappingCategoryDialogOpen(true);
  };

  const handleSaveMappingCategory = async () => {
    if (!mappingCategoryFormData.name.trim()) {
      toast.error('Name is required');
      return;
    }
    
    setSaving(true);
    try {
      const url = editingMappingCategory 
        ? `${API_URL}/api/admin/mapping-categories/${editingMappingCategory.id}`
        : `${API_URL}/api/admin/mapping-categories`;
      
      const response = await fetch(url, {
        method: editingMappingCategory ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(mappingCategoryFormData)
      });
      
      if (!response.ok) throw new Error('Failed to save mapping category');
      toast.success(editingMappingCategory ? 'Mapping category updated' : 'Mapping category added');
      setMappingCategoryDialogOpen(false);
      fetchMappingCategories();
    } catch (error) {
      toast.error('Failed to save mapping category');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteMappingCategory = (categoryId, categoryName) => {
    setDeleteTarget({ type: 'mapping_category', id: categoryId, name: categoryName });
    setDeleteConfirmOpen(true);
  };

  const handleMoveMappingCategory = async (categoryIndex, direction) => {
    const categories = [...mappingCategories];
    const newIndex = categoryIndex + direction;
    
    if (newIndex < 0 || newIndex >= categories.length) return;
    
    [categories[categoryIndex], categories[newIndex]] = [categories[newIndex], categories[categoryIndex]];
    setMappingCategories(categories);
    
    try {
      const response = await fetch(
        `${API_URL}/api/admin/mapping-categories/reorder`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ category_ids: categories.map(c => c.id) })
        }
      );
      if (!response.ok) throw new Error('Failed to reorder');
      fetchMappingCategories();
    } catch (error) {
      toast.error('Failed to reorder mapping categories');
    }
  };

  const handleAddQuestion = () => {
    setEditingQuestion(null);
    setQuestionDialogOpen(true);
  };

  const handleEditQuestion = (question) => {
    setEditingQuestion(question);
    setQuestionDialogOpen(true);
  };

  const handleSaveQuestion = async (formData, editing) => {
    if (!formData.text.trim()) {
      toast.error('Question text is required');
      return;
    }
    
    setSaving(true);
    try {
      if (editing) {
        const response = await fetch(
          `${API_URL}/api/admin/question-library/${questionLibraryContext}/${editing.id}`,
          {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(formData)
          }
        );
        if (!response.ok) throw new Error('Failed to update');
        toast.success('Question updated');
      } else {
        const response = await fetch(
          `${API_URL}/api/admin/question-library/${questionLibraryContext}`,
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(formData)
          }
        );
        if (!response.ok) throw new Error('Failed to create');
        toast.success('Question added to library');
      }
      
      setQuestionDialogOpen(false);
      setEditingQuestion(null);
      fetchQuestionLibrary();
    } catch (error) {
      toast.error('Failed to save question');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteQuestion = (questionId, questionText) => {
    setDeleteTarget({ type: 'question', id: questionId, name: questionText });
    setDeleteConfirmOpen(true);
  };

  const handleMoveQuestion = async (questionIndex, direction) => {
    const questions = questionLibraryContext === 'about_me' ? [...aboutMeQuestions] 
      : questionLibraryContext === 'public' ? [...publicQuestions] 
      : [...aboutOthersQuestions];
    const newIndex = questionIndex + direction;
    
    // Check bounds
    if (newIndex < 0 || newIndex >= questions.length) return;
    
    // Swap questions
    [questions[questionIndex], questions[newIndex]] = [questions[newIndex], questions[questionIndex]];
    
    // Optimistically update UI
    if (questionLibraryContext === 'about_me') {
      setAboutMeQuestions(questions);
    } else if (questionLibraryContext === 'public') {
      setPublicQuestions(questions);
    } else {
      setAboutOthersQuestions(questions);
    }
    
    // Get new order of question IDs
    const questionIds = questions.map(q => q.id);
    
    try {
      const response = await fetch(
        `${API_URL}/api/admin/question-library/${questionLibraryContext}/reorder`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ question_ids: questionIds })
        }
      );
      if (!response.ok) throw new Error('Failed to reorder');
    } catch (error) {
      toast.error('Failed to reorder questions');
      // Revert on error
      fetchQuestionLibrary();
    }
  };

  const addOption = () => {
    if (newOption.trim()) {
      setQuestionFormData(prev => ({
        ...prev,
        options: [...prev.options, newOption.trim()]
      }));
      setNewOption('');
    }
  };

  const removeOption = (index) => {
    setQuestionFormData(prev => ({
      ...prev,
      options: prev.options.filter((_, i) => i !== index)
    }));
  };

  const getQuestionTypeLabel = (type) => {
    return QUESTION_TYPES.find(t => t.value === type)?.label || type;
  };

  // ============== Customer Functions ==============
  const fetchCustomers = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/customers`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch customers');
      const data = await response.json();
      setCustomers(data);
    } catch (error) {
      toast.error('Failed to load customer accounts');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomerDetails = async (customerId) => {
    setDetailsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/customers/${customerId}/details`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch details');
      const data = await response.json();
      setCustomerDetails(data);
    } catch (error) {
      toast.error('Failed to load customer details');
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleViewCustomer = async (customer) => {
    setSelectedCustomer(customer);
    await fetchCustomerDetails(customer.id);
  };

  const handleImpersonate = async (customer) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/impersonate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ user_id: customer.id })
      });
      
      if (!response.ok) throw new Error('Failed to impersonate');
      
      const data = await response.json();
      
      localStorage.setItem('originalToken', token);
      localStorage.setItem('impersonating', JSON.stringify(data.impersonating));
      
      setToken(data.access_token);
      setUser({
        id: data.impersonating.id,
        email: data.impersonating.email,
        first_name: data.impersonating.first_name,
        last_name: data.impersonating.last_name,
        organization_id: data.impersonating.organization_id,
        is_global_admin: true
      });
      
      toast.success(`Now viewing as ${data.impersonating.email}`);
      window.location.href = '/dashboard';
    } catch (error) {
      toast.error('Failed to switch view');
      console.error(error);
    }
  };

  const handleEditTrialDate = (customer) => {
    setSelectedCustomer(customer);
    const existingDate = customer.trial_end_date ? 
      customer.trial_end_date.split('T')[0] : 
      new Date().toISOString().split('T')[0];
    setNewTrialEndDate(existingDate);
    setEditTrialOpen(true);
  };

  const handleSaveTrialDate = async () => {
    if (!selectedCustomer || !newTrialEndDate) return;
    
    setSavingTrialDate(true);
    try {
      const isoDate = new Date(newTrialEndDate + 'T23:59:59.999Z').toISOString();
      
      const response = await fetch(
        `${API_URL}/api/admin/customers/${selectedCustomer.id}/trial-end-date`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ trial_end_date: isoDate })
        }
      );
      
      if (!response.ok) throw new Error('Failed to update trial date');
      
      toast.success('Trial end date updated');
      setEditTrialOpen(false);
      fetchCustomers();
    } catch (error) {
      toast.error('Failed to update trial date');
    } finally {
      setSavingTrialDate(false);
    }
  };

  const handleResetToFree = (customer) => {
    setResetFreeTarget(customer);
    setResetFreeMaxUsers(100);
    setResetFreeOpen(true);
  };

  const handleConfirmResetToFree = async () => {
    if (!resetFreeTarget) return;
    setAdminActionLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/customers/${resetFreeTarget.id}/reset-to-free`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: resetFreeTarget.id, max_users: resetFreeMaxUsers }),
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.detail || 'Failed'); }
      toast.success(`${resetFreeTarget.name} reset to Free Trial (${resetFreeMaxUsers} users)`);
      setResetFreeOpen(false);
      fetchCustomers();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setAdminActionLoading(false);
    }
  };

  const handleOverrideMaxUsers = (customer) => {
    setOverrideMaxTarget(customer);
    setOverrideMaxUsers(customer.stakeholder_count || 100);
    setOverrideMaxOpen(true);
  };

  const handleConfirmOverrideMaxUsers = async () => {
    if (!overrideMaxTarget) return;
    setAdminActionLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/customers/${overrideMaxTarget.id}/override-max-users`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: overrideMaxTarget.id, max_users: overrideMaxUsers }),
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.detail || 'Failed'); }
      toast.success(`${overrideMaxTarget.name} max users set to ${overrideMaxUsers}`);
      setOverrideMaxOpen(false);
      fetchCustomers();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setAdminActionLoading(false);
    }
  };

  const handleViewInvoiceHistory = async (customer) => {
    setInvoiceHistoryTarget(customer);
    setInvoiceHistoryLoading(true);
    setInvoiceHistoryOpen(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/customers/${customer.id}/invoices`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      setInvoiceHistoryData(await res.json());
    } catch {
      toast.error('Failed to load invoice history');
      setInvoiceHistoryOpen(false);
    } finally {
      setInvoiceHistoryLoading(false);
    }
  };

  // Consolidated Account Details popup handler
  const handleOpenAccountDetails = (customer) => {
    setAccountDetailsTarget(customer);
    setAccountType(customer.account_type || 'user_managed');
    setEnterpriseMaxUsers(customer.max_users_override || customer.paid_users || 100);
    setEnterpriseMonthlyRevenue(customer.monthly_revenue != null ? String(customer.monthly_revenue) : '');
    setIsUnlimited(customer.is_unlimited || false);
    setTrialEndDate(customer.trial_end_date ? customer.trial_end_date.split('T')[0] : '');
    setAccountDetailsOpen(true);
    fetchAccountProjects(customer.id);
  };

  const fetchAccountProjects = async (userId) => {
    setAccountProjectsLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/customers/${userId}/details`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to fetch projects');
      const data = await res.json();
      setAccountProjects(data.projects || []);
    } catch {
      setAccountProjects([]);
    } finally {
      setAccountProjectsLoading(false);
    }
  };

  const handleTransferProject = async () => {
    if (!transferProject || !transferTargetId) return;
    setTransferring(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/transfer-project`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ project_id: transferProject.id, target_owner_id: transferTargetId }),
      });
      if (!res.ok) {
        const d = await res.json();
        throw new Error(d.detail || 'Transfer failed');
      }
      toast.success(`Project "${transferProject.name}" transferred successfully`);
      setTransferDialogOpen(false);
      setTransferProject(null);
      setTransferTargetId('');
      // Refresh projects for current account
      if (accountDetailsTarget) fetchAccountProjects(accountDetailsTarget.id);
      fetchCustomers();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setTransferring(false);
    }
  };

  const handleSaveAccountDetails = async () => {
    if (!accountDetailsTarget) return;
    setAccountDetailsLoading(true);
    try {
      // Update account type if changed
      const res = await fetch(`${API_URL}/api/admin/customers/${accountDetailsTarget.id}/account-type`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          account_type: accountType,
          max_users: accountType === 'enterprise' && !isUnlimited ? enterpriseMaxUsers : null,
          is_unlimited: accountType === 'enterprise' && isUnlimited,
          monthly_revenue: accountType === 'enterprise' && enterpriseMonthlyRevenue !== '' ? parseFloat(enterpriseMonthlyRevenue) : null,
        }),
      });
      if (!res.ok) {
        const d = await res.json();
        throw new Error(d.detail || 'Failed to update account type');
      }

      // Update trial date if changed and not enterprise
      if (trialEndDate && accountType !== 'enterprise') {
        const isoDate = new Date(trialEndDate + 'T23:59:59.999Z').toISOString();
        await fetch(`${API_URL}/api/admin/customers/${accountDetailsTarget.id}/trial-end-date`, {
          method: 'PUT',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ trial_end_date: isoDate }),
        });
      }

      toast.success('Account details updated successfully');
      setAccountDetailsOpen(false);
      fetchCustomers();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setAccountDetailsLoading(false);
    }
  };

  // ============== Global Defaults Functions ==============
  const fetchGlobalDefaults = async () => {
    setDefaultsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/global-defaults`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch defaults');
      const data = await response.json();
      setGlobalDefaults(data);
    } catch (error) {
      toast.error('Failed to load global defaults');
    } finally {
      setDefaultsLoading(false);
    }
  };

  const handleAddSegmentType = () => {
    setEditingType(null);
    setTypeFormData({ name: '', description: '', icon: '' });
    setTypeDialogOpen(true);
  };

  const handleEditSegmentType = (type) => {
    setEditingType(type);
    setTypeFormData({ name: type.name, description: type.description || '', icon: type.icon || '' });
    setTypeDialogOpen(true);
  };

  const handleSaveSegmentType = async () => {
    if (!typeFormData.name.trim()) {
      toast.error('Name is required');
      return;
    }
    
    setSaving(true);
    try {
      if (editingType) {
        // Update existing
        const response = await fetch(
          `${API_URL}/api/admin/global-defaults/segment-types/${editingType.id}`,
          {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(typeFormData)
          }
        );
        if (!response.ok) throw new Error('Failed to update');
        toast.success('Segment type updated');
      } else {
        // Create new
        const response = await fetch(
          `${API_URL}/api/admin/global-defaults/segment-types`,
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(typeFormData)
          }
        );
        if (!response.ok) throw new Error('Failed to create');
        toast.success('Segment type added');
      }
      
      setTypeDialogOpen(false);
      fetchGlobalDefaults();
    } catch (error) {
      toast.error('Failed to save segment type');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSegmentType = (typeId, typeName) => {
    console.log('Opening delete confirmation for:', typeName);
    setDeleteTarget({ type: 'segment_type', id: typeId, name: typeName });
    setDeleteConfirmOpen(true);
  };

  const handleDeleteSegment = (typeId, segmentId, segmentName) => {
    console.log('Opening delete confirmation for segment:', segmentName);
    setDeleteTarget({ type: 'segment', typeId, id: segmentId, name: segmentName });
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    console.log('Confirming delete for:', deleteTarget);
    try {
      let url;
      let successMessage;
      let refreshFunc;
      
      if (deleteTarget.type === 'segment_type') {
        url = `${API_URL}/api/admin/global-defaults/segment-types/${deleteTarget.id}`;
        successMessage = 'Segment type deleted';
        refreshFunc = fetchGlobalDefaults;
      } else if (deleteTarget.type === 'segment') {
        url = `${API_URL}/api/admin/global-defaults/segment-types/${deleteTarget.typeId}/segments/${deleteTarget.id}`;
        successMessage = 'Segment deleted';
        refreshFunc = fetchGlobalDefaults;
      } else if (deleteTarget.type === 'driver') {
        url = `${API_URL}/api/admin/driver-library/${deleteTarget.id}`;
        successMessage = 'Driver deleted';
        refreshFunc = fetchDriverLibrary;
      } else if (deleteTarget.type === 'mapping_category') {
        url = `${API_URL}/api/admin/mapping-categories/${deleteTarget.id}`;
        successMessage = 'Mapping category deleted';
        refreshFunc = fetchMappingCategories;
      } else if (deleteTarget.type === 'question') {
        url = `${API_URL}/api/admin/question-library/${questionLibraryContext}/${deleteTarget.id}`;
        successMessage = 'Question deleted';
        refreshFunc = fetchQuestionLibrary;
      } else {
        return;
      }

      const response = await fetch(url, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      console.log('Delete response:', response.status);
      if (!response.ok) throw new Error('Failed to delete');
      
      toast.success(successMessage);
      refreshFunc();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete');
    } finally {
      setDeleteConfirmOpen(false);
      setDeleteTarget({ type: null, id: null, name: '' });
    }
  };

  const handleAddSegment = (type) => {
    setSelectedTypeForSegment(type);
    setEditingSegment(null);
    setSegmentFormData({ name: '', description: '', icon: '' });
    setSegmentDialogOpen(true);
  };

  const handleEditSegment = (type, segment) => {
    setSelectedTypeForSegment(type);
    setEditingSegment(segment);
    setSegmentFormData({
      name: segment.name,
      description: segment.description || '',
      icon: segment.icon || ''
    });
    setSegmentDialogOpen(true);
  };

  const handleSaveSegment = async () => {
    if (!segmentFormData.name.trim()) {
      toast.error('Name is required');
      return;
    }
    
    setSaving(true);
    try {
      const url = editingSegment 
        ? `${API_URL}/api/admin/global-defaults/segment-types/${selectedTypeForSegment.id}/segments/${editingSegment.id}`
        : `${API_URL}/api/admin/global-defaults/segment-types/${selectedTypeForSegment.id}/segments`;
      
      const response = await fetch(url, {
        method: editingSegment ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(segmentFormData)
      });
      if (!response.ok) throw new Error(`Failed to ${editingSegment ? 'update' : 'add'} segment`);
      toast.success(`Segment ${editingSegment ? 'updated' : 'added'}`);
      setSegmentDialogOpen(false);
      fetchGlobalDefaults();
    } catch (error) {
      toast.error(`Failed to ${editingSegment ? 'update' : 'add'} segment`);
    } finally {
      setSaving(false);
    }
  };

  const handleMoveSegment = async (type, segmentIndex, direction) => {
    const segments = [...(type.default_segments || [])];
    const newIndex = segmentIndex + direction;
    
    // Check bounds
    if (newIndex < 0 || newIndex >= segments.length) return;
    
    // Swap segments
    [segments[segmentIndex], segments[newIndex]] = [segments[newIndex], segments[segmentIndex]];
    
    // Get new order of segment IDs
    const segmentIds = segments.map(s => s.id);
    
    try {
      const response = await fetch(
        `${API_URL}/api/admin/global-defaults/segment-types/${type.id}/segments/reorder`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ segment_ids: segmentIds })
        }
      );
      if (!response.ok) throw new Error('Failed to reorder');
      fetchGlobalDefaults();
    } catch (error) {
      toast.error('Failed to reorder segments');
    }
  };

  const toggleTypeExpanded = (typeId) => {
    setExpandedTypes(prev => ({ ...prev, [typeId]: !prev[typeId] }));
  };

  // ============== Helper Functions ==============
  const getStatusBadge = (status) => {
    switch (status) {
      case 'trial':
        return <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100"><Clock className="h-3 w-3 mr-1" /> Trial</Badge>;
      case 'active':
        return <Badge className="bg-green-100 text-green-700 hover:bg-green-100"><CheckCircle className="h-3 w-3 mr-1" /> Active</Badge>;
      case 'expired':
        return <Badge className="bg-red-100 text-red-700 hover:bg-red-100"><AlertTriangle className="h-3 w-3 mr-1" /> Expired</Badge>;
      case 'enterprise':
        return <Badge className="bg-purple-100 text-purple-700 hover:bg-purple-100"><Building2 className="h-3 w-3 mr-1" /> Enterprise</Badge>;
      case 'cancelled':
        return <Badge className="bg-slate-100 text-slate-700 hover:bg-slate-100"><X className="h-3 w-3 mr-1" /> Cancelled</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return 'N/A';
    }
  };

  const filteredCustomers = customers.filter(customer => 
    customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    customer.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    customer.organization_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="p-6 space-y-6" data-testid="customer-accounts-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <Shield className="h-6 w-6 text-[#5AA2AE]" />
            Global Administration
          </h1>
          <p className="text-slate-500 mt-1">Manage customer accounts and system defaults</p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full max-w-3xl grid-cols-5">
          <TabsTrigger value="customers" className="gap-2" data-testid="customers-tab">
            <Users className="h-4 w-4" />
            Customer Accounts
          </TabsTrigger>
          <TabsTrigger value="defaults" className="gap-2" data-testid="defaults-tab">
            <Settings className="h-4 w-4" />
            Global Defaults
          </TabsTrigger>
          <TabsTrigger value="questions" className="gap-2" data-testid="questions-tab">
            <BookOpen className="h-4 w-4" />
            Question Library
          </TabsTrigger>
          <TabsTrigger value="demo" className="gap-2" data-testid="demo-tab">
            <Eye className="h-4 w-4" />
            Demo
          </TabsTrigger>
          <TabsTrigger value="invoices" className="gap-2" data-testid="invoices-tab">
            <CreditCard className="h-4 w-4" />
            Invoices
          </TabsTrigger>
        </TabsList>

        {/* Customer Accounts Tab */}
        <TabsContent value="customers" className="space-y-4 mt-4">
          {/* Search and Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="md:col-span-2">
              <CardContent className="pt-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Search by name, email, or organization..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                    data-testid="customer-search-input"
                  />
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-4 flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-100">
                  <Users className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{customers.length}</p>
                  <p className="text-xs text-slate-500">Total Accounts</p>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-4 flex items-center gap-3">
                <div className="p-2 rounded-lg bg-amber-100">
                  <Clock className="h-5 w-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{customers.filter(c => c.status === 'trial').length}</p>
                  <p className="text-xs text-slate-500">Active Trials</p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Customer Table */}
          <Card>
            <CardContent className="p-0">
              {loading ? (
                <div className="flex items-center justify-center p-12">
                  <RefreshCw className="h-8 w-8 animate-spin text-slate-400" />
                </div>
              ) : filteredCustomers.length === 0 ? (
                <div className="flex flex-col items-center justify-center p-12 text-slate-500">
                  <Users className="h-12 w-12 mb-4 text-slate-300" />
                  <p>No customer accounts found</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Customer</TableHead>
                      <TableHead>Organization</TableHead>
                      <TableHead>Account Type</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead>Trial End</TableHead>
                      <TableHead className="text-center">Active Projects</TableHead>
                      <TableHead className="text-center">Invited</TableHead>
                      <TableHead className="text-center">Paid</TableHead>
                      <TableHead className="text-right">Monthly Revenue</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredCustomers.map((customer) => (
                      <TableRow key={customer.id} data-testid={`customer-row-${customer.id}`}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{customer.name}</p>
                            <p className="text-sm text-slate-500">{customer.email}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Building2 className="h-4 w-4 text-slate-400" />
                            {customer.organization_name}
                          </div>
                        </TableCell>
                        <TableCell>
                          {customer.account_type === 'enterprise' ? (
                            <Badge className="bg-purple-100 text-purple-700 border-purple-200">
                              Enterprise {customer.is_unlimited && '(∞)'}
                            </Badge>
                          ) : customer.account_type === 'demo' ? (
                            <Badge className="bg-amber-100 text-amber-700 border-amber-200">
                              Demo
                            </Badge>
                          ) : (
                            <Badge variant="outline">User Managed</Badge>
                          )}
                        </TableCell>
                        <TableCell>{getStatusBadge(customer.status)}</TableCell>
                        <TableCell>
                          <span className="text-sm text-slate-600">{formatDate(customer.created_at)}</span>
                        </TableCell>
                        <TableCell>{customer.account_type !== 'enterprise' ? formatDate(customer.trial_end_date) : '—'}</TableCell>
                        <TableCell className="text-center" data-testid={`active-projects-${customer.id}`}>
                          <Badge variant="outline">{customer.active_project_count ?? 0}</Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge variant="outline">{customer.invited_users ?? customer.stakeholder_count}</Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge variant="outline">{customer.is_unlimited ? '∞' : (customer.paid_users || 0)}</Badge>
                        </TableCell>
                        <TableCell className="text-right" data-testid={`monthly-revenue-${customer.id}`}>
                          {customer.monthly_revenue != null 
                            ? <span className="font-medium text-emerald-600">${customer.monthly_revenue.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</span>
                            : <span className="text-slate-400">—</span>
                          }
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleOpenAccountDetails(customer)}
                              data-testid={`account-details-${customer.id}`}
                              title="Account Details"
                            >
                              <Settings className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleViewInvoiceHistory(customer)}
                              data-testid={`invoice-history-${customer.id}`}
                              title="Invoice History"
                            >
                              <CreditCard className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleImpersonate(customer)}
                              className="gap-1"
                              data-testid={`impersonate-${customer.id}`}
                            >
                              View As
                              <ChevronRight className="h-3 w-3" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Global Defaults Tab */}
        <TabsContent value="defaults" className="space-y-4 mt-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Layers className="h-5 w-5 text-[#5AA2AE]" />
                    Default Segment Types & Segments
                  </CardTitle>
                  <CardDescription>
                    Configure the default segment types and segments that will be available in the library for all new projects.
                  </CardDescription>
                </div>
                <Button onClick={handleAddSegmentType} className="bg-[#5AA2AE] hover:bg-[#5AA2AE]/90 gap-2" data-testid="add-segment-type-btn">
                  <Plus className="h-4 w-4" />
                  Add Segment Type
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {defaultsLoading ? (
                <div className="flex items-center justify-center p-12">
                  <RefreshCw className="h-8 w-8 animate-spin text-slate-400" />
                </div>
              ) : globalDefaults.segment_types.length === 0 ? (
                <div className="flex flex-col items-center justify-center p-12 text-slate-500 border-2 border-dashed rounded-lg">
                  <Layers className="h-12 w-12 mb-4 text-slate-300" />
                  <p className="font-medium">No default segment types configured</p>
                  <p className="text-sm mt-1">Add segment types that will appear in the library for all projects</p>
                  <Button onClick={handleAddSegmentType} variant="outline" className="mt-4 gap-2">
                    <Plus className="h-4 w-4" />
                    Add Your First Segment Type
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {globalDefaults.segment_types.map((type) => {
                    const TypeIcon = getIconByName(type.icon);
                    const isExpanded = expandedTypes[type.id] !== false;
                    
                    return (
                      <Collapsible key={type.id} open={isExpanded} onOpenChange={() => toggleTypeExpanded(type.id)}>
                        <div className="border rounded-lg">
                          <div className="flex items-center justify-between p-4 hover:bg-slate-50 transition-colors">
                            {/* Clickable area for collapsible - LEFT SIDE */}
                            <CollapsibleTrigger asChild>
                              <div className="flex items-center gap-3 flex-1 cursor-pointer">
                                <div className="w-10 h-10 rounded-lg bg-[#5AA2AE]/10 flex items-center justify-center">
                                  <TypeIcon className="h-5 w-5 text-[#5AA2AE]" />
                                </div>
                                <div>
                                  <p className="font-medium text-slate-900">{type.name}</p>
                                  <p className="text-sm text-slate-500">{type.description || 'No description'}</p>
                                </div>
                                <Badge variant="outline" className="ml-2">
                                  {type.default_segments?.length || 0} segments
                                </Badge>
                                {isExpanded ? <ChevronDown className="h-5 w-5 text-slate-400 ml-auto" /> : <ChevronRight className="h-5 w-5 text-slate-400 ml-auto" />}
                              </div>
                            </CollapsibleTrigger>
                            {/* Action buttons - OUTSIDE CollapsibleTrigger */}
                            <div className="flex items-center gap-2 ml-4">
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={() => handleEditSegmentType(type)} 
                                data-testid={`edit-type-${type.id}`}
                                title="Edit segment type"
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={() => handleDeleteSegmentType(type.id, type.name)} 
                                className="text-red-600 hover:text-red-700 hover:bg-red-50" 
                                data-testid={`delete-type-${type.id}`}
                                title="Delete segment type"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                          <CollapsibleContent>
                            <div className="border-t px-4 py-3 bg-slate-50/50">
                              <div className="flex items-center justify-between mb-3">
                                <p className="text-sm font-medium text-slate-600">Default Segments</p>
                                <Button variant="outline" size="sm" onClick={() => handleAddSegment(type)} className="gap-1" data-testid={`add-segment-${type.id}`}>
                                  <Plus className="h-3 w-3" />
                                  Add Segment
                                </Button>
                              </div>
                              {type.default_segments?.length > 0 ? (
                                <div className="space-y-2">
                                  {type.default_segments.map((segment, index) => {
                                    const SegIcon = getIconByName(segment.icon || type.icon);
                                    return (
                                      <div key={segment.id} className="flex items-center justify-between p-3 bg-white rounded-lg border group">
                                        <div className="flex items-center gap-3">
                                          {/* Reorder buttons */}
                                          <div className="flex flex-col gap-0.5">
                                            <Button
                                              variant="ghost"
                                              size="sm"
                                              onClick={() => handleMoveSegment(type, index, -1)}
                                              disabled={index === 0}
                                              className="h-5 w-5 p-0 text-slate-400 hover:text-slate-600 disabled:opacity-30"
                                            >
                                              <ChevronUp className="h-3 w-3" />
                                            </Button>
                                            <Button
                                              variant="ghost"
                                              size="sm"
                                              onClick={() => handleMoveSegment(type, index, 1)}
                                              disabled={index === type.default_segments.length - 1}
                                              className="h-5 w-5 p-0 text-slate-400 hover:text-slate-600 disabled:opacity-30"
                                            >
                                              <ChevronDown className="h-3 w-3" />
                                            </Button>
                                          </div>
                                          <div className="w-8 h-8 rounded-md bg-slate-100 flex items-center justify-center">
                                            <SegIcon className="h-4 w-4 text-slate-600" />
                                          </div>
                                          <div>
                                            <p className="text-sm font-medium">{segment.name}</p>
                                            {segment.description && (
                                              <p className="text-xs text-slate-500 truncate max-w-[200px]">{segment.description}</p>
                                            )}
                                          </div>
                                        </div>
                                        <div className="flex items-center gap-1">
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => handleEditSegment(type, segment)}
                                            className="text-slate-600 hover:text-slate-800 h-8 w-8 p-0"
                                            data-testid={`edit-segment-${segment.id}`}
                                          >
                                            <Edit className="h-4 w-4" />
                                          </Button>
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => handleDeleteSegment(type.id, segment.id, segment.name)}
                                            className="text-red-600 hover:text-red-700 h-8 w-8 p-0"
                                            data-testid={`delete-segment-${segment.id}`}
                                          >
                                            <Trash2 className="h-4 w-4" />
                                          </Button>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              ) : (
                                <p className="text-sm text-slate-500 py-2">No default segments. Add segments that should be created automatically with this type.</p>
                              )}
                            </div>
                          </CollapsibleContent>
                        </div>
                      </Collapsible>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Driver Library Card */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Target className="h-5 w-5 text-[#5AA2AE]" />
                    Driver Library
                  </CardTitle>
                  <CardDescription>
                    Manage default drivers that can be associated with questions.
                  </CardDescription>
                </div>
                <Button onClick={handleAddDriver} className="bg-[#5AA2AE] hover:bg-[#5AA2AE]/90 gap-2">
                  <Plus className="h-4 w-4" />
                  Add Driver
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {libraryDrivers.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-4">No drivers in the library. Add drivers to categorize questions.</p>
              ) : (
                <div className="space-y-2">
                  {libraryDrivers.map((driver, index) => {
                    const DriverIcon = getIconByName(driver.icon);
                    const iconColorClass = getDriverIconColor(driver.icon);
                    return (
                      <div key={driver.id} className="flex items-center gap-3 px-4 py-3 bg-white rounded-lg border group hover:shadow-sm transition-shadow">
                        {/* Reorder buttons */}
                        <div className="flex flex-col gap-0.5">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleMoveDriver(index, -1)}
                            disabled={index === 0}
                            className="h-5 w-5 p-0 text-slate-400 hover:text-slate-600 disabled:opacity-30"
                          >
                            <ChevronUp className="h-3 w-3" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleMoveDriver(index, 1)}
                            disabled={index === libraryDrivers.length - 1}
                            className="h-5 w-5 p-0 text-slate-400 hover:text-slate-600 disabled:opacity-30"
                          >
                            <ChevronDown className="h-3 w-3" />
                          </Button>
                        </div>
                        {/* Icon */}
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${iconColorClass}`}>
                          <DriverIcon className="h-5 w-5" />
                        </div>
                        {/* Driver info */}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-900">{driver.name}</p>
                          {driver.description && (
                            <p className="text-xs text-slate-500 truncate">{driver.description}</p>
                          )}
                        </div>
                        {/* Edit/Delete buttons */}
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditDriver(driver)}
                            className="h-7 w-7 p-0"
                          >
                            <Edit className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteDriver(driver.id, driver.name)}
                            className="h-7 w-7 p-0 text-red-600"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Mapping Category Library Card */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Link className="h-5 w-5 text-[#5AA2AE]" />
                    Mapping Category Library
                  </CardTitle>
                  <CardDescription>
                    Define relationship types for stakeholder mapping (e.g., My Leaders, My Peers).
                  </CardDescription>
                </div>
                <Button onClick={handleAddMappingCategory} className="bg-[#5AA2AE] hover:bg-[#5AA2AE]/90 gap-2">
                  <Plus className="h-4 w-4" />
                  Add Category
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {mappingCategories.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-4">No mapping categories defined. Add categories to define stakeholder relationships.</p>
              ) : (
                <div className="space-y-2">
                  {mappingCategories.map((category, index) => {
                    const CategoryIcon = getIconByName(category.icon);
                    const iconColorClass = getDriverIconColor(category.icon);
                    return (
                      <div key={category.id} className="flex items-center gap-3 px-4 py-3 bg-white rounded-lg border group hover:shadow-sm transition-shadow">
                        {/* Reorder buttons */}
                        <div className="flex flex-col gap-0.5">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleMoveMappingCategory(index, -1)}
                            disabled={index === 0}
                            className="h-5 w-5 p-0 text-slate-400 hover:text-slate-600 disabled:opacity-30"
                          >
                            <ChevronUp className="h-3 w-3" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleMoveMappingCategory(index, 1)}
                            disabled={index === mappingCategories.length - 1}
                            className="h-5 w-5 p-0 text-slate-400 hover:text-slate-600 disabled:opacity-30"
                          >
                            <ChevronDown className="h-3 w-3" />
                          </Button>
                        </div>
                        {/* Icon */}
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${iconColorClass}`}>
                          <CategoryIcon className="h-5 w-5" />
                        </div>
                        {/* Category info */}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-900">{category.name}</p>
                          {category.description && (
                            <p className="text-xs text-slate-500 truncate">{category.description}</p>
                          )}
                        </div>
                        {/* Edit/Delete buttons */}
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditMappingCategory(category)}
                            className="h-7 w-7 p-0"
                          >
                            <Edit className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteMappingCategory(category.id, category.name)}
                            className="h-7 w-7 p-0 text-red-600"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Question Library Tab */}
        <TabsContent value="questions" className="space-y-4 mt-4">
          {/* Questions Card */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <BookOpen className="h-5 w-5 text-[#5AA2AE]" />
                    Question Library
                  </CardTitle>
                  <CardDescription>
                    Manage default questions that can be imported into any project.
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* Context Toggle */}
              <div className="flex gap-2 mb-6">
                <Button
                  variant={questionLibraryContext === 'about_me' ? 'default' : 'outline'}
                  onClick={() => { setQuestionLibraryContext('about_me'); setDriverFilter('all'); }}
                  className={questionLibraryContext === 'about_me' ? 'bg-[#5AA2AE] hover:bg-[#5AA2AE]/90' : ''}
                >
                  <User className="mr-2 h-4 w-4" />
                  About Me
                </Button>
                <Button
                  variant={questionLibraryContext === 'about_others' ? 'default' : 'outline'}
                  onClick={() => { setQuestionLibraryContext('about_others'); setDriverFilter('all'); }}
                  className={questionLibraryContext === 'about_others' ? 'bg-[#5AA2AE] hover:bg-[#5AA2AE]/90' : ''}
                >
                  <UsersRound className="mr-2 h-4 w-4" />
                  About Others
                </Button>
                <Button
                  variant={questionLibraryContext === 'public' ? 'default' : 'outline'}
                  onClick={() => { setQuestionLibraryContext('public'); setDriverFilter('all'); }}
                  className={questionLibraryContext === 'public' ? 'bg-[#5AA2AE] hover:bg-[#5AA2AE]/90' : ''}
                  data-testid="question-library-public-toggle"
                >
                  <Globe className="mr-2 h-4 w-4" />
                  Public
                </Button>
              </div>

              {/* Driver Filter (for About Me and Public) */}
              {(questionLibraryContext === 'about_me' || questionLibraryContext === 'public') && libraryDrivers.length > 0 && (
                <div className="flex items-center gap-2 mb-4">
                  <Label className="text-sm text-slate-600">Filter by Driver:</Label>
                  <Select value={driverFilter} onValueChange={setDriverFilter}>
                    <SelectTrigger className="w-[200px]">
                      <SelectValue placeholder="All Drivers" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Drivers</SelectItem>
                      <SelectItem value="none">No Driver</SelectItem>
                      {libraryDrivers.map((driver) => (
                        <SelectItem key={driver.id} value={driver.id}>{driver.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Add Question Button */}
              <div className="flex justify-end mb-4">
                <Button onClick={handleAddQuestion} className="bg-[#5AA2AE] hover:bg-[#5AA2AE]/90 gap-2">
                  <Plus className="h-4 w-4" />
                  Add Question
                </Button>
              </div>

              {/* Questions List */}
              {questionsLoading ? (
                <div className="flex items-center justify-center p-12">
                  <RefreshCw className="h-8 w-8 animate-spin text-slate-400" />
                </div>
              ) : (() => {
                // Get questions based on context
                const allQuestions = questionLibraryContext === 'about_me' ? aboutMeQuestions 
                  : questionLibraryContext === 'public' ? publicQuestions 
                  : aboutOthersQuestions;
                
                // Apply driver filter for About Me
                const filteredQuestions = (questionLibraryContext === 'about_me' || questionLibraryContext === 'public') && driverFilter !== 'all'
                  ? allQuestions.filter(q => driverFilter === 'none' ? !q.driver_id : q.driver_id === driverFilter)
                  : allQuestions;
                
                if (filteredQuestions.length === 0) {
                  return (
                    <div className="flex flex-col items-center justify-center p-12 text-slate-500 border-2 border-dashed rounded-lg">
                      <FileQuestion className="h-12 w-12 mb-4 text-slate-300" />
                      <p className="font-medium">
                        {driverFilter !== 'all' 
                          ? `No questions matching the selected filter`
                          : `No questions in library for ${questionLibraryContext === 'about_me' ? 'About Me' : questionLibraryContext === 'public' ? 'Public' : 'About Others'}`
                        }
                      </p>
                      <p className="text-sm mt-1">Add questions that project admins can import</p>
                      <Button onClick={handleAddQuestion} variant="outline" className="mt-4 gap-2">
                        <Plus className="h-4 w-4" />
                        Add Your First Question
                      </Button>
                    </div>
                  );
                }
                
                return (
                  <div className="space-y-3">
                    {filteredQuestions.map((question, index) => {
                      const questionDriver = libraryDrivers.find(d => d.id === question.driver_id);
                      // Get index in original array for reordering
                      const originalIndex = allQuestions.findIndex(q => q.id === question.id);
                      return (
                        <div key={question.id} className="flex items-start justify-between p-4 bg-white rounded-lg border group hover:shadow-sm transition-shadow">
                          {/* Reorder buttons */}
                          <div className="flex flex-col gap-0.5 mr-3">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleMoveQuestion(originalIndex, -1)}
                              disabled={originalIndex === 0 || driverFilter !== 'all'}
                              className="h-5 w-5 p-0 text-slate-400 hover:text-slate-600 disabled:opacity-30"
                              title={driverFilter !== 'all' ? 'Clear filter to reorder' : 'Move up'}
                            >
                              <ChevronUp className="h-3 w-3" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleMoveQuestion(originalIndex, 1)}
                              disabled={originalIndex === allQuestions.length - 1 || driverFilter !== 'all'}
                              className="h-5 w-5 p-0 text-slate-400 hover:text-slate-600 disabled:opacity-30"
                              title={driverFilter !== 'all' ? 'Clear filter to reorder' : 'Move down'}
                            >
                              <ChevronDown className="h-3 w-3" />
                            </Button>
                          </div>
                          <div className="flex-1">
                            {question.heading && (
                              <p className="text-xs font-semibold text-[#5AA2AE] uppercase tracking-wide mb-1">
                                {question.heading}
                              </p>
                            )}
                            <p className="text-sm font-medium text-slate-900">{question.text}</p>
                            <div className="flex flex-wrap items-center gap-2 mt-2">
                              <Badge variant="secondary" className="text-xs">
                                {getQuestionTypeLabel(question.question_type)}
                              </Badge>
                              {questionDriver && (
                                <Badge variant="outline" className="text-xs border-[#5AA2AE] text-[#5AA2AE]">
                                  <Target className="h-3 w-3 mr-1" />
                                  {questionDriver.name}
                                </Badge>
                              )}
                              {question.required && (
                                <Badge variant="outline" className="text-xs border-amber-300 text-amber-600">
                                  Required
                                </Badge>
                              )}
                              {question.allow_skip && (
                                <Badge variant="outline" className="text-xs border-slate-300 text-slate-500">
                                  Skippable
                                </Badge>
                              )}
                              {question.quick_start && (
                                <Badge variant="outline" className="text-xs border-[#5AA2AE] bg-[#5AA2AE]/10 text-[#5AA2AE]">
                                  <Rocket className="h-3 w-3 mr-1" />
                                  Quick Start
                                </Badge>
                              )}
                              {question.options?.length > 0 && (
                                <span className="text-xs text-slate-400">
                                  {question.options.length} options
                                </span>
                              )}
                              {question.question_type === 'slider' && question.slider_config && (
                                <span className="text-xs text-slate-400">
                                  {question.slider_config.left_label} → {question.slider_config.right_label}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditQuestion(question)}
                              className="h-8 w-8 p-0"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteQuestion(question.id, question.text)}
                              className="text-red-600 hover:text-red-700 h-8 w-8 p-0"
                              data-testid={`delete-question-${question.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Demo Tab */}
        <TabsContent value="demo" className="space-y-4 mt-4" data-testid="demo-tab-content">
          {/* Demo Project Configuration (Experience Pulse) */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Eye className="h-5 w-5 text-[#5AA2AE]" />
                    Experience Pulse — Demo Projects
                  </CardTitle>
                  <CardDescription>Configure which projects appear in the "Experience Pulse" feature on the public landing page.</CardDescription>
                </div>
                <Button
                  size="sm"
                  onClick={() => {
                    setDemoConfigForm({ project_id: '', display_title: '', short_description: '', icon: 'Building2', sequence: demoProjects.length });
                    setDemoConfigDialogOpen(true);
                  }}
                  className="bg-[#5AA2AE] hover:bg-[#5AA2AE]/90 gap-1"
                  data-testid="add-demo-project-btn"
                >
                  <Plus className="h-4 w-4" /> Add Demo Project
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {demoProjects.length === 0 ? (
                <p className="text-sm text-slate-500 py-4 text-center">No demo projects configured yet. Add a project to get started.</p>
              ) : (
                <div className="space-y-2">
                  {demoProjects.sort((a, b) => (a.sequence || 0) - (b.sequence || 0)).map((dp) => {
                    const IconComp = DEMO_ICON_MAP[dp.icon] || Building2;
                    const isVisible = dp.visible !== false;
                    return (
                      <div key={dp.project_id} className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${isVisible ? 'border-slate-100 bg-white hover:border-slate-200' : 'border-slate-100 bg-slate-50/60'}`} data-testid={`demo-project-config-${dp.project_id}`}>
                        <div className={`flex items-center justify-center w-9 h-9 rounded-lg flex-shrink-0 ${isVisible ? 'bg-[#5AA2AE]/10' : 'bg-slate-200/60'}`}>
                          <IconComp className={`h-4 w-4 ${isVisible ? 'text-[#5AA2AE]' : 'text-slate-400'}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className={`text-sm font-semibold ${isVisible ? 'text-slate-800' : 'text-slate-400'}`}>{dp.display_title}</span>
                            <Badge variant="outline" className="text-xs py-0">{dp.project_name}</Badge>
                            <Badge variant="outline" className="text-xs py-0 bg-slate-50">#{dp.sequence + 1}</Badge>
                            {!isVisible && <Badge variant="outline" className="text-xs py-0 bg-amber-50 text-amber-600 border-amber-200">Hidden</Badge>}
                          </div>
                          <p className="text-xs text-slate-500 truncate">{dp.short_description}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          {/* Visibility toggle */}
                          <div className="flex items-center gap-1.5" title={isVisible ? 'Visible on demo page' : 'Hidden from demo page'}>
                            <span className="text-xs text-slate-400">{isVisible ? 'Visible' : 'Hidden'}</span>
                            <Switch
                              checked={isVisible}
                              onCheckedChange={async (checked) => {
                                try {
                                  await fetch(`${API_URL}/api/admin/demo-projects/${dp.project_id}/visibility`, {
                                    method: 'PATCH',
                                    headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                                    body: JSON.stringify({ visible: checked }),
                                  });
                                  setDemoProjects(prev => prev.map(p => p.project_id === dp.project_id ? { ...p, visible: checked } : p));
                                  toast.success(checked ? 'Project visible on demo page' : 'Project hidden from demo page');
                                } catch { toast.error('Failed to update visibility'); }
                              }}
                              data-testid={`demo-visibility-toggle-${dp.project_id}`}
                            />
                          </div>
                          <div className="w-px h-6 bg-slate-200" />
                          {/* Export snapshot */}
                          <Button
                            variant="ghost" size="sm"
                            className="h-8 gap-1 text-xs text-slate-500 hover:text-slate-700"
                            disabled={exportingProject === dp.project_id}
                            onClick={async () => {
                              setExportingProject(dp.project_id);
                              try {
                                const res = await fetch(`${API_URL}/api/admin/demo-projects/${dp.project_id}/export-snapshot`, {
                                  method: 'POST', headers: { 'Authorization': `Bearer ${token}` },
                                });
                                const data = await res.json();
                                if (res.ok) toast.success('Snapshot exported');
                                else toast.error(data.detail || 'Export failed');
                              } catch { toast.error('Export failed'); }
                              finally { setExportingProject(null); }
                            }}
                            data-testid={`export-demo-project-${dp.project_id}`}
                          >
                            {exportingProject === dp.project_id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Cloud className="h-3 w-3" />}
                            Export
                          </Button>
                          {/* Seed data */}
                          <Button
                            variant="outline" size="sm"
                            className="h-8 gap-1 text-xs border-[#5AA2AE]/30 text-[#5AA2AE] hover:bg-[#5AA2AE]/5"
                            disabled={seedingProject === dp.project_id}
                            onClick={async () => {
                              if (!confirm(`Seed all demo data for "${dp.display_title}"? This will overwrite existing project data.`)) return;
                              setSeedingProject(dp.project_id);
                              try {
                                const res = await fetch(`${API_URL}/api/admin/seed-demo-data/${dp.project_id}`, {
                                  method: 'POST', headers: { 'Authorization': `Bearer ${token}` },
                                });
                                const data = await res.json();
                                if (res.ok) toast.success(`Demo data seeded for ${data.project}`);
                                else toast.error(data.detail || 'Seed failed');
                              } catch { toast.error('Seed failed'); }
                              finally { setSeedingProject(null); }
                            }}
                            data-testid={`seed-demo-project-${dp.project_id}`}
                          >
                            {seedingProject === dp.project_id ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                            Seed Data
                          </Button>
                          <div className="w-px h-6 bg-slate-200" />
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => {
                            setDemoConfigForm({
                              project_id: dp.project_id,
                              display_title: dp.display_title,
                              short_description: dp.short_description,
                              icon: dp.icon,
                              sequence: dp.sequence || 0,
                            });
                            setDemoConfigDialogOpen(true);
                          }} data-testid={`edit-demo-project-${dp.project_id}`}>
                            <Pencil className="h-4 w-4 text-slate-400" />
                          </Button>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-red-500 hover:text-red-600" onClick={async () => {
                            if (!confirm(`Remove "${dp.display_title}" from Experience Pulse?`)) return;
                            await fetch(`${API_URL}/api/admin/demo-projects/${dp.project_id}`, {
                              method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` }
                            });
                            setDemoProjects(prev => prev.filter(p => p.project_id !== dp.project_id));
                            toast.success('Demo project removed');
                          }} data-testid={`delete-demo-project-${dp.project_id}`}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Demo Users List */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Demo Users</CardTitle>
              <CardDescription>Users who have accessed demo projects via Experience Pulse. This list persists after their 14-day access expires.</CardDescription>
            </CardHeader>
            <CardContent>
              {demoUsers.length === 0 ? (
                <p className="text-sm text-slate-500">No demo users yet.</p>
              ) : (
                <div className="border rounded-md overflow-hidden">
                  <table className="w-full text-sm" data-testid="demo-users-table">
                    <thead className="bg-slate-50 border-b">
                      <tr>
                        <th className="px-4 py-2 text-left font-medium text-slate-600">Name</th>
                        <th className="px-4 py-2 text-left font-medium text-slate-600">Email</th>
                        <th className="px-4 py-2 text-left font-medium text-slate-600">Project</th>
                        <th className="px-4 py-2 text-left font-medium text-slate-600">Signed Up</th>
                        <th className="px-4 py-2 text-left font-medium text-slate-600">Expires</th>
                        <th className="px-4 py-2 text-left font-medium text-slate-600">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {demoUsers.map(du => {
                        const isActive = du.demo_expires_at ? new Date(du.demo_expires_at) > new Date() : false;
                        return (
                          <tr key={du.id} className="border-b last:border-0 hover:bg-slate-50/50">
                            <td className="px-4 py-2">{du.first_name} {du.last_name}</td>
                            <td className="px-4 py-2 text-slate-600">{du.email}</td>
                            <td className="px-4 py-2">{du.project_name}</td>
                            <td className="px-4 py-2 text-slate-500">{du.created_at ? new Date(du.created_at).toLocaleDateString() : '-'}</td>
                            <td className="px-4 py-2 text-slate-500">{du.demo_expires_at ? new Date(du.demo_expires_at).toLocaleDateString() : '-'}</td>
                            <td className="px-4 py-2">
                              <Badge variant={isActive ? 'default' : 'secondary'} className={isActive ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}>
                                {isActive ? 'Active' : 'Expired'}
                              </Badge>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Demo Project Access Tracking */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Experience Pulse Activity</CardTitle>
              <CardDescription>Tracks which demo projects visitors access from the landing page.</CardDescription>
            </CardHeader>
            <CardContent>
              {demoProjectAccess.length === 0 ? (
                <p className="text-sm text-slate-500">No activity yet.</p>
              ) : (
                <div className="border rounded-md overflow-hidden">
                  <table className="w-full text-sm" data-testid="demo-access-table">
                    <thead className="bg-slate-50 border-b">
                      <tr>
                        <th className="px-4 py-2 text-left font-medium text-slate-600">Project</th>
                        <th className="px-4 py-2 text-left font-medium text-slate-600">Visitor</th>
                        <th className="px-4 py-2 text-left font-medium text-slate-600">Accessed</th>
                      </tr>
                    </thead>
                    <tbody>
                      {demoProjectAccess.map(a => (
                        <tr key={a.id} className="border-b last:border-0 hover:bg-slate-50/50">
                          <td className="px-4 py-2">{a.display_title || a.project_name}</td>
                          <td className="px-4 py-2 text-slate-600">{a.email || a.name || 'Anonymous'}</td>
                          <td className="px-4 py-2 text-slate-500">{a.accessed_at ? new Date(a.accessed_at).toLocaleString() : '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>


        {/* Invoices Tab */}
        <TabsContent value="invoices" className="space-y-4 mt-4">
          <AdminInvoicesTab />
        </TabsContent>
      </Tabs>

      {/* Customer Details Dialog */}
      <Dialog open={!!selectedCustomer && !editTrialOpen} onOpenChange={(open) => !open && setSelectedCustomer(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-[#5AA2AE]" />
              Customer Details
            </DialogTitle>
            <DialogDescription>
              {selectedCustomer?.organization_name}
            </DialogDescription>
          </DialogHeader>
          
          {detailsLoading ? (
            <div className="flex items-center justify-center p-8">
              <RefreshCw className="h-6 w-6 animate-spin text-slate-400" />
            </div>
          ) : customerDetails ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-500 text-xs">Account Owner</Label>
                  <p className="font-medium">{customerDetails.user?.first_name} {customerDetails.user?.last_name}</p>
                  <p className="text-sm text-slate-500">{customerDetails.user?.email}</p>
                </div>
                <div>
                  <Label className="text-slate-500 text-xs">Organization</Label>
                  <p className="font-medium">{customerDetails.organization?.name}</p>
                  <p className="text-sm text-slate-500">
                    Trial ends: {formatDate(customerDetails.organization?.trial_ends_at)}
                  </p>
                </div>
              </div>

              <div>
                <Label className="text-slate-500 text-xs mb-2 block">Projects ({customerDetails.projects?.length || 0})</Label>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {customerDetails.projects?.map((project) => (
                    <div key={project.id} className="p-3 bg-slate-50 rounded-lg flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <FolderOpen className="h-4 w-4 text-slate-400" />
                        <span className="font-medium">{project.name}</span>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-slate-500">
                        <span>{project.stakeholder_count} stakeholders</span>
                        <span>{project.question_count} questions</span>
                        <span>{project.response_count} responses</span>
                      </div>
                    </div>
                  ))}
                  {(!customerDetails.projects || customerDetails.projects.length === 0) && (
                    <p className="text-slate-500 text-sm p-3">No projects created yet</p>
                  )}
                </div>
              </div>
            </div>
          ) : null}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectedCustomer(null)}>
              Close
            </Button>
            <Button 
              onClick={() => handleImpersonate(selectedCustomer)}
              className="bg-[#5AA2AE] hover:bg-[#5AA2AE]/90"
            >
              View As This Customer
              <ChevronRight className="ml-2 h-4 w-4" />
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Trial Date Dialog */}
      <Dialog open={editTrialOpen} onOpenChange={setEditTrialOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-[#5AA2AE]" />
              Edit Trial End Date
            </DialogTitle>
            <DialogDescription>
              Change the trial end date for {selectedCustomer?.organization_name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Current End Date</Label>
              <p className="text-sm text-slate-500">{formatDate(selectedCustomer?.trial_end_date)}</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-trial-date">New End Date</Label>
              <Input
                id="new-trial-date"
                type="date"
                value={newTrialEndDate}
                onChange={(e) => setNewTrialEndDate(e.target.value)}
                data-testid="trial-date-input"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditTrialOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSaveTrialDate}
              disabled={savingTrialDate}
              className="bg-[#5AA2AE] hover:bg-[#5AA2AE]/90"
              data-testid="save-trial-date-btn"
            >
              {savingTrialDate ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset to Free Plan Dialog */}
      <Dialog open={resetFreeOpen} onOpenChange={setResetFreeOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RefreshCw className="h-5 w-5 text-amber-500" />
              Reset to Free Plan
            </DialogTitle>
            <DialogDescription>
              Reset {resetFreeTarget?.name} ({resetFreeTarget?.organization_name}) back to a Free Trial.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="reset-max-users">Max Users for Free Trial</Label>
              <Input
                id="reset-max-users"
                type="number"
                min={1}
                value={resetFreeMaxUsers}
                onChange={(e) => setResetFreeMaxUsers(parseInt(e.target.value) || 100)}
                data-testid="reset-free-max-users-input"
              />
              <p className="text-xs text-slate-500">Default is 100. Override to allow more users during the trial.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetFreeOpen(false)}>Cancel</Button>
            <Button
              onClick={handleConfirmResetToFree}
              disabled={adminActionLoading}
              className="bg-amber-500 hover:bg-amber-600"
              data-testid="confirm-reset-free-btn"
            >
              {adminActionLoading ? 'Resetting...' : 'Reset to Free Plan'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Override Max Users Dialog */}
      <Dialog open={overrideMaxOpen} onOpenChange={setOverrideMaxOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-[#5AA2AE]" />
              Override Max Users
            </DialogTitle>
            <DialogDescription>
              Set a custom maximum user limit for {overrideMaxTarget?.name} ({overrideMaxTarget?.organization_name}).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="override-max-users">New Max Users</Label>
              <Input
                id="override-max-users"
                type="number"
                min={1}
                value={overrideMaxUsers}
                onChange={(e) => setOverrideMaxUsers(parseInt(e.target.value) || 100)}
                data-testid="override-max-users-input"
              />
              <p className="text-xs text-slate-500">Current stakeholders: {overrideMaxTarget?.stakeholder_count || 0}</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOverrideMaxOpen(false)}>Cancel</Button>
            <Button
              onClick={handleConfirmOverrideMaxUsers}
              disabled={adminActionLoading}
              className="bg-[#5AA2AE] hover:bg-[#5AA2AE]/90"
              data-testid="confirm-override-max-btn"
            >
              {adminActionLoading ? 'Saving...' : 'Override Max Users'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Segment Type Dialog */}
      <Dialog open={typeDialogOpen} onOpenChange={setTypeDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingType ? 'Edit Segment Type' : 'Add Default Segment Type'}
            </DialogTitle>
            <DialogDescription>
              {editingType ? 'Update this segment type' : 'Add a new default segment type to the library'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="type-name">Name *</Label>
              <Input
                id="type-name"
                placeholder="e.g., Involvement, Location, Organization"
                value={typeFormData.name}
                onChange={(e) => setTypeFormData({ ...typeFormData, name: e.target.value })}
                data-testid="type-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="type-description">Description</Label>
              <Textarea
                id="type-description"
                placeholder="Describe this segment type..."
                value={typeFormData.description}
                onChange={(e) => setTypeFormData({ ...typeFormData, description: e.target.value })}
                rows={2}
              />
            </div>
            <div className="space-y-2">
              <Label>Icon</Label>
              <div className="flex flex-wrap gap-2 p-2 border rounded-lg bg-slate-50">
                {AVAILABLE_ICONS.map(({ name, icon: Icon, label }) => (
                  <button
                    key={name}
                    type="button"
                    onClick={() => setTypeFormData({ ...typeFormData, icon: typeFormData.icon === name ? '' : name })}
                    className={`p-2 rounded-lg transition-all ${
                      typeFormData.icon === name 
                        ? 'bg-[#5AA2AE] text-white ring-2 ring-[#5AA2AE]/30' 
                        : 'bg-white hover:bg-slate-100 text-slate-600'
                    }`}
                    title={label}
                  >
                    <Icon className="h-5 w-5" />
                  </button>
                ))}
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setTypeDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSaveSegmentType}
              disabled={saving}
              className="bg-[#5AA2AE] hover:bg-[#5AA2AE]/90"
              data-testid="save-type-btn"
            >
              {saving ? 'Saving...' : (editingType ? 'Update' : 'Add Segment Type')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Segment Dialog */}
      <Dialog open={segmentDialogOpen} onOpenChange={setSegmentDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingSegment ? 'Edit Segment' : 'Add Default Segment'}</DialogTitle>
            <DialogDescription>
              {editingSegment ? `Update "${editingSegment.name}"` : `Add a segment to "${selectedTypeForSegment?.name}"`}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="segment-name">Name *</Label>
              <Input
                id="segment-name"
                placeholder="e.g., Team Member, North Region"
                value={segmentFormData.name}
                onChange={(e) => setSegmentFormData({ ...segmentFormData, name: e.target.value })}
                data-testid="segment-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="segment-description">Description</Label>
              <Textarea
                id="segment-description"
                placeholder="Describe this segment..."
                value={segmentFormData.description}
                onChange={(e) => setSegmentFormData({ ...segmentFormData, description: e.target.value })}
                rows={2}
              />
            </div>
            <div className="space-y-2">
              <Label>Icon (optional - will use type's icon if not set)</Label>
              <div className="flex flex-wrap gap-2 p-2 border rounded-lg bg-slate-50">
                {AVAILABLE_ICONS.map(({ name, icon: Icon, label }) => (
                  <button
                    key={name}
                    type="button"
                    onClick={() => setSegmentFormData({ ...segmentFormData, icon: segmentFormData.icon === name ? '' : name })}
                    className={`p-2 rounded-lg transition-all ${
                      segmentFormData.icon === name 
                        ? 'bg-[#5AA2AE] text-white ring-2 ring-[#5AA2AE]/30' 
                        : 'bg-white hover:bg-slate-100 text-slate-600'
                    }`}
                    title={label}
                  >
                    <Icon className="h-5 w-5" />
                  </button>
                ))}
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setSegmentDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSaveSegment}
              disabled={saving}
              className="bg-[#5AA2AE] hover:bg-[#5AA2AE]/90"
              data-testid="save-segment-btn"
            >
              {saving ? 'Saving...' : (editingSegment ? 'Update Segment' : 'Add Segment')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Memoized Question Dialog */}
      <QuestionLibraryDialog
        open={questionDialogOpen}
        onOpenChange={setQuestionDialogOpen}
        editingQuestion={editingQuestion}
        questionLibraryContext={questionLibraryContext}
        libraryDrivers={libraryDrivers}
        onSave={handleSaveQuestion}
        saving={saving}
      />


      {/* Delete Confirmation AlertDialog */}
      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="h-5 w-5" />
              Confirm Delete
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deleteTarget.name}"?
              {deleteTarget.type === 'segment_type' && ' This will also delete all segments within this type.'}
              {deleteTarget.type === 'driver' && ' Questions associated with this driver will no longer have a driver assigned.'}
              {deleteTarget.type === 'mapping_category' && ' This category will be removed from all stakeholder relationships.'}
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Add/Edit Driver Dialog */}
      <Dialog open={driverDialogOpen} onOpenChange={setDriverDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingDriver ? 'Edit Driver' : 'Add Driver to Library'}</DialogTitle>
            <DialogDescription>
              {editingDriver ? 'Update this driver' : 'Add a new driver to categorize questions'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="driver-name">Name *</Label>
              <Input
                id="driver-name"
                placeholder="e.g., Communication, Leadership, Culture"
                value={driverFormData.name}
                onChange={(e) => setDriverFormData({ ...driverFormData, name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="driver-description">Description</Label>
              <Textarea
                id="driver-description"
                placeholder="Describe this driver..."
                value={driverFormData.description}
                onChange={(e) => setDriverFormData({ ...driverFormData, description: e.target.value })}
                rows={2}
              />
            </div>
            <div className="space-y-2">
              <Label>Icon</Label>
              <div className="flex flex-wrap gap-2 p-3 border rounded-lg bg-slate-50 max-h-40 overflow-y-auto">
                {AVAILABLE_ICONS.slice(0, 16).map(({ name, icon: Icon, label }) => {
                  const colorClass = getDriverIconColor(name);
                  return (
                    <button
                      key={name}
                      type="button"
                      onClick={() => setDriverFormData({ ...driverFormData, icon: driverFormData.icon === name ? '' : name })}
                      className={`p-2 rounded-lg transition-all ${
                        driverFormData.icon === name 
                          ? 'ring-2 ring-[#5AA2AE] ring-offset-2' 
                          : 'hover:scale-110'
                      } ${colorClass}`}
                      title={label}
                    >
                      <Icon className="h-5 w-5" />
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setDriverDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSaveDriver}
              disabled={saving || !driverFormData.name.trim()}
              className="bg-[#5AA2AE] hover:bg-[#5AA2AE]/90"
            >
              {saving ? 'Saving...' : (editingDriver ? 'Update' : 'Add Driver')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mapping Category Dialog */}
      <Dialog open={mappingCategoryDialogOpen} onOpenChange={setMappingCategoryDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingMappingCategory ? 'Edit Mapping Category' : 'Add Mapping Category'}</DialogTitle>
            <DialogDescription>
              {editingMappingCategory ? 'Update this relationship type' : 'Define a new relationship type for stakeholder mapping'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="mapping-category-name">Name *</Label>
              <Input
                id="mapping-category-name"
                placeholder="e.g., My Leaders, My Peers, Decision Maker"
                value={mappingCategoryFormData.name}
                onChange={(e) => setMappingCategoryFormData({ ...mappingCategoryFormData, name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="mapping-category-description">Description</Label>
              <Textarea
                id="mapping-category-description"
                placeholder="Describe this relationship type..."
                value={mappingCategoryFormData.description}
                onChange={(e) => setMappingCategoryFormData({ ...mappingCategoryFormData, description: e.target.value })}
                rows={2}
              />
            </div>
            <div className="space-y-2">
              <Label>Icon</Label>
              <div className="flex flex-wrap gap-2 p-3 border rounded-lg bg-slate-50 max-h-40 overflow-y-auto">
                {AVAILABLE_ICONS.slice(0, 16).map(({ name, icon: Icon, label }) => {
                  const colorClass = getDriverIconColor(name);
                  return (
                    <button
                      key={name}
                      type="button"
                      onClick={() => setMappingCategoryFormData({ ...mappingCategoryFormData, icon: mappingCategoryFormData.icon === name ? '' : name })}
                      className={`p-2 rounded-lg transition-all ${
                        mappingCategoryFormData.icon === name 
                          ? 'ring-2 ring-[#5AA2AE] ring-offset-2' 
                          : 'hover:scale-110'
                      } ${colorClass}`}
                      title={label}
                    >
                      <Icon className="h-5 w-5" />
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setMappingCategoryDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSaveMappingCategory}
              disabled={saving || !mappingCategoryFormData.name.trim()}
              className="bg-[#5AA2AE] hover:bg-[#5AA2AE]/90"
            >
              {saving ? 'Saving...' : (editingMappingCategory ? 'Update' : 'Add Category')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invoice History Dialog */}
      <Dialog open={invoiceHistoryOpen} onOpenChange={setInvoiceHistoryOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-[#5AA2AE]" />
              Invoice History — {invoiceHistoryTarget?.name}
            </DialogTitle>
            <DialogDescription>
              {invoiceHistoryTarget?.organization_name}
            </DialogDescription>
          </DialogHeader>
          {invoiceHistoryLoading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-8 w-8 animate-spin text-slate-400" />
            </div>
          ) : invoiceHistoryData ? (
            <div className="space-y-6">
              {/* Revenue Metrics */}
              <div className="grid grid-cols-3 gap-4">
                <Card>
                  <CardContent className="pt-4 text-center">
                    <p className="text-2xl font-bold text-green-600">${invoiceHistoryData.total_revenue?.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</p>
                    <p className="text-xs text-slate-500 mt-1">Total Revenue</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 text-center">
                    <p className="text-2xl font-bold text-red-500">${invoiceHistoryData.total_refunded?.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</p>
                    <p className="text-xs text-slate-500 mt-1">Total Refunded</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 text-center">
                    <p className="text-2xl font-bold text-slate-900">${invoiceHistoryData.net_revenue?.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</p>
                    <p className="text-xs text-slate-500 mt-1">Net Revenue</p>
                  </CardContent>
                </Card>
              </div>

              {/* Monthly Chart */}
              {invoiceHistoryData.monthly_chart?.length > 0 && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-slate-600">Monthly Payments</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={220}>
                      <BarChart data={invoiceHistoryData.monthly_chart}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="#94a3b8" />
                        <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" tickFormatter={v => `$${v}`} />
                        <Tooltip
                          formatter={(v, name) => [`$${v.toLocaleString()}`, name === 'revenue' ? 'Revenue' : 'Refunds']}
                          labelFormatter={l => `Month: ${l}`}
                        />
                        <Bar dataKey="revenue" fill="#5AA2AE" radius={[4, 4, 0, 0]} name="revenue" />
                        <Bar dataKey="refunds" fill="#ef4444" radius={[4, 4, 0, 0]} name="refunds" />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}

              {/* Transaction List */}
              <div>
                <p className="text-sm font-medium text-slate-600 mb-2">{invoiceHistoryData.transaction_count} Transaction(s)</p>
                {invoiceHistoryData.transactions?.length === 0 ? (
                  <p className="text-slate-400 text-sm py-4 text-center">No transactions found.</p>
                ) : (
                  <div className="border rounded-lg overflow-hidden max-h-[240px] overflow-auto">
                    <table className="w-full text-sm">
                      <thead className="sticky top-0 bg-slate-50">
                        <tr className="border-b">
                          <th className="text-left p-3 font-medium text-slate-600">Description</th>
                          <th className="text-right p-3 font-medium text-slate-600 w-[120px]">Amount</th>
                          <th className="text-center p-3 font-medium text-slate-600 w-[100px]">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {invoiceHistoryData.transactions?.map((tx, i) => (
                          <tr key={i} className="border-b last:border-b-0 hover:bg-slate-50/50">
                            <td className="p-3">
                              <p className="font-medium text-slate-900">
                                {tx.type === 'user_limit_upgrade' ? 'User Limit Upgrade' :
                                  tx.package_id ? `${tx.package_id.charAt(0).toUpperCase() + tx.package_id.slice(1)} Plan` : 'Payment'}
                              </p>
                              <p className="text-xs text-slate-500">{new Date(tx.created_at).toLocaleDateString()}</p>
                              {tx.payment_status === 'refunded' && tx.refunded_at && (
                                <p className="text-xs text-red-500">Refunded on {new Date(tx.refunded_at).toLocaleDateString()}</p>
                              )}
                            </td>
                            <td className="p-3 text-right">
                              <span className={`font-semibold ${tx.payment_status === 'refunded' ? 'line-through text-slate-400' : 'text-slate-900'}`}>
                                ${(tx.amount || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                              </span>
                              {tx.payment_status === 'refunded' && tx.refund_amount > 0 && (
                                <p className="text-red-600 font-semibold text-xs mt-0.5">
                                  -${(tx.refund_amount || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                                </p>
                              )}
                            </td>
                            <td className="p-3 text-center">
                              <Badge
                                className={
                                  tx.payment_status === 'paid' ? 'bg-green-100 text-green-700' :
                                  tx.payment_status === 'refunded' ? 'bg-red-100 text-red-700' :
                                  'bg-amber-100 text-amber-700'
                                }
                              >
                                {tx.payment_status || tx.status}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Account Details Dialog (Consolidated) */}
      <Dialog open={accountDetailsOpen} onOpenChange={setAccountDetailsOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5 text-[#5AA2AE]" />
              Account Details
            </DialogTitle>
            <DialogDescription>
              {accountDetailsTarget?.name} — {accountDetailsTarget?.organization_name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-6 py-4">
            {/* Current Info */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
              <div>
                <p className="text-slate-500">Email</p>
                <p className="font-medium truncate">{accountDetailsTarget?.email}</p>
              </div>
              <div>
                <p className="text-slate-500">Invited Users</p>
                <p className="font-medium">{accountDetailsTarget?.invited_users}</p>
              </div>
              <div>
                <p className="text-slate-500">Projects</p>
                <p className="font-medium">{accountDetailsTarget?.project_count}</p>
              </div>
              <div>
                <p className="text-slate-500">Current Status</p>
                <p className="font-medium capitalize">{accountDetailsTarget?.status}</p>
              </div>
            </div>

            {/* Projects Section */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-medium">Projects</Label>
                {accountProjectsLoading && <RefreshCw className="h-4 w-4 animate-spin text-slate-400" />}
              </div>
              {accountProjects.length > 0 ? (
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b">
                      <tr>
                        <th className="text-left px-3 py-2 font-medium text-slate-600">Project</th>
                        <th className="text-center px-3 py-2 font-medium text-slate-600 hidden sm:table-cell">Stakeholders</th>
                        <th className="text-center px-3 py-2 font-medium text-slate-600 hidden sm:table-cell">Questions</th>
                        <th className="text-center px-3 py-2 font-medium text-slate-600 hidden sm:table-cell">Responses</th>
                        <th className="text-right px-3 py-2 font-medium text-slate-600">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {accountProjects.map(p => (
                        <tr key={p.id} className="hover:bg-slate-50">
                          <td className="px-3 py-2">
                            <p className="font-medium text-slate-900 truncate max-w-[200px]" title={p.name}>{p.name}</p>
                            <p className="text-xs text-slate-400">{p.created_at ? new Date(p.created_at).toLocaleDateString() : ''}</p>
                          </td>
                          <td className="text-center px-3 py-2 text-slate-600 hidden sm:table-cell">{p.stakeholder_count || 0}</td>
                          <td className="text-center px-3 py-2 text-slate-600 hidden sm:table-cell">{p.question_count || 0}</td>
                          <td className="text-center px-3 py-2 text-slate-600 hidden sm:table-cell">{p.response_count || 0}</td>
                          <td className="text-right px-3 py-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-xs text-[#5AA2AE] hover:text-[#5AA2AE]/80"
                              onClick={() => {
                                setTransferProject(p);
                                setTransferTargetId('');
                                setTransferDialogOpen(true);
                              }}
                              data-testid={`transfer-project-${p.id}`}
                            >
                              Transfer
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : !accountProjectsLoading ? (
                <p className="text-sm text-slate-400 py-2">No projects found</p>
              ) : null}
            </div>

            {/* Account Type Toggle */}
            <div className="space-y-3">
              <Label className="text-sm font-medium">Account Type</Label>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant={accountType === 'user_managed' ? 'default' : 'outline'}
                  className={accountType === 'user_managed' ? 'bg-[#5AA2AE] hover:bg-[#4a8a94]' : ''}
                  onClick={() => setAccountType('user_managed')}
                  size="sm"
                >
                  User Managed
                </Button>
                <Button
                  type="button"
                  variant={accountType === 'enterprise' ? 'default' : 'outline'}
                  className={accountType === 'enterprise' ? 'bg-purple-600 hover:bg-purple-700' : ''}
                  onClick={() => setAccountType('enterprise')}
                  size="sm"
                >
                  Enterprise
                </Button>
                <Button
                  type="button"
                  variant={accountType === 'demo' ? 'default' : 'outline'}
                  className={accountType === 'demo' ? 'bg-amber-500 hover:bg-amber-600 text-white' : ''}
                  onClick={() => setAccountType('demo')}
                  size="sm"
                >
                  Demo
                </Button>
              </div>
              <p className="text-xs text-slate-500">
                {accountType === 'user_managed' 
                  ? 'Subscription managed via Stripe. User pays for their plan.'
                  : accountType === 'enterprise'
                    ? 'Manual management. No Stripe billing required.'
                    : 'Demo account. Public landing visitors auto-invited, access expires in 14 days.'}
              </p>
            </div>

            {/* Enterprise Settings */}
            {accountType === 'enterprise' && (
              <div className="space-y-4 p-4 bg-purple-50 rounded-lg border border-purple-100">
                <div className="flex items-center gap-3">
                  <Checkbox
                    id="unlimited"
                    checked={isUnlimited}
                    onCheckedChange={setIsUnlimited}
                  />
                  <Label htmlFor="unlimited" className="text-sm font-medium">Unlimited Users</Label>
                </div>
                {!isUnlimited && (
                  <div className="space-y-2">
                    <Label className="text-sm">Max Users</Label>
                    <Input
                      type="number"
                      value={enterpriseMaxUsers}
                      onChange={(e) => setEnterpriseMaxUsers(parseInt(e.target.value) || 0)}
                      min={1}
                    />
                  </div>
                )}
                <div className="space-y-2">
                  <Label className="text-sm">Monthly Revenue ($)</Label>
                  <Input
                    type="number"
                    placeholder="e.g. 5000"
                    value={enterpriseMonthlyRevenue}
                    onChange={(e) => setEnterpriseMonthlyRevenue(e.target.value)}
                    min={0}
                    step="0.01"
                    data-testid="enterprise-monthly-revenue"
                  />
                  <p className="text-xs text-slate-500">Manually entered revenue for this enterprise account.</p>
                </div>
              </div>
            )}

            {/* Trial Date (only for user managed) */}
            {accountType === 'user_managed' && (
              <div className="space-y-2">
                <Label className="text-sm font-medium">Trial End Date</Label>
                <Input
                  type="date"
                  value={trialEndDate}
                  onChange={(e) => setTrialEndDate(e.target.value)}
                />
              </div>
            )}

            {/* Reset to User Managed info */}
            {accountType === 'user_managed' && accountDetailsTarget?.account_type === 'enterprise' && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-xs text-amber-700">
                  <strong>Note:</strong> Switching back to User Managed will clear the enterprise override. 
                  The account will need to subscribe via Stripe.
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAccountDetailsOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSaveAccountDetails} 
              disabled={accountDetailsLoading}
              className="bg-[#5AA2AE] hover:bg-[#4a8a94]"
            >
              {accountDetailsLoading ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : null}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Transfer Project Dialog */}
      <Dialog open={transferDialogOpen} onOpenChange={setTransferDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowRightLeft className="h-5 w-5 text-[#5AA2AE]" />
              Transfer Project
            </DialogTitle>
            <DialogDescription>
              Move "{transferProject?.name}" to a different account owner.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-xs text-amber-700">
                <strong>Warning:</strong> This will move the project and all its data (stakeholders, questions, responses) 
                to the selected account. The original owner will no longer have access.
              </p>
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium">Transfer to Account Owner</Label>
              <Select value={transferTargetId} onValueChange={setTransferTargetId}>
                <SelectTrigger data-testid="transfer-target-select">
                  <SelectValue placeholder="Select account owner..." />
                </SelectTrigger>
                <SelectContent>
                  {customers
                    .filter(c => c.id !== accountDetailsTarget?.id)
                    .map(c => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name} ({c.organization_name})
                      </SelectItem>
                    ))
                  }
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTransferDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleTransferProject}
              disabled={!transferTargetId || transferring}
              className="bg-[#5AA2AE] hover:bg-[#4a8a94]"
              data-testid="confirm-transfer-btn"
            >
              {transferring ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : null}
              Transfer Project
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Demo Project Config Dialog */}
      <Dialog open={demoConfigDialogOpen} onOpenChange={setDemoConfigDialogOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="demo-config-dialog">
          <DialogHeader>
            <DialogTitle>{demoConfigForm.project_id && demoProjects.some(p => p.project_id === demoConfigForm.project_id) ? 'Edit' : 'Add'} Demo Project</DialogTitle>
            <DialogDescription>Configure how this project appears in Experience Pulse.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Project</Label>
              <Select value={demoConfigForm.project_id} onValueChange={(v) => {
                const proj = allProjects.find(p => p.id === v);
                setDemoConfigForm(f => ({
                  ...f,
                  project_id: v,
                  display_title: f.display_title || proj?.name || '',
                }));
              }}>
                <SelectTrigger data-testid="demo-config-project-select"><SelectValue placeholder="Select a project" /></SelectTrigger>
                <SelectContent>
                  {allProjects.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Display Title <span className="text-xs text-slate-400">(shown in Experience Pulse)</span></Label>
              <Input
                value={demoConfigForm.display_title}
                onChange={(e) => setDemoConfigForm(f => ({ ...f, display_title: e.target.value }))}
                placeholder="e.g. Airport Terminal Refurbishment"
                data-testid="demo-config-title-input"
              />
            </div>
            <div>
              <Label>Short Description</Label>
              <Textarea
                value={demoConfigForm.short_description}
                onChange={(e) => setDemoConfigForm(f => ({ ...f, short_description: e.target.value }))}
                placeholder="A brief description for the Experience Pulse card"
                rows={2}
                data-testid="demo-config-desc-input"
              />
            </div>
            <div>
              <Label>Icon</Label>
              <div className="grid grid-cols-8 gap-1.5 mt-1 p-2 border rounded-lg bg-slate-50 max-h-36 overflow-y-auto">
                {DEMO_ICON_OPTIONS.map(name => {
                  const Ic = DEMO_ICON_MAP[name];
                  return (
                    <button
                      key={name}
                      type="button"
                      onClick={() => setDemoConfigForm(f => ({ ...f, icon: name }))}
                      className={`p-2 rounded-md flex items-center justify-center transition-all ${demoConfigForm.icon === name ? 'bg-[#5AA2AE] text-white ring-2 ring-[#5AA2AE]/30' : 'hover:bg-slate-100 text-slate-500'}`}
                      title={name}
                      data-testid={`demo-icon-${name}`}
                    >
                      <Ic className="h-4 w-4" />
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <Label>Display Order</Label>
              <Input
                type="number"
                value={demoConfigForm.sequence}
                onChange={(e) => setDemoConfigForm(f => ({ ...f, sequence: parseInt(e.target.value) || 0 }))}
                min={0}
                data-testid="demo-config-sequence-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDemoConfigDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={async () => {
                if (!demoConfigForm.project_id || !demoConfigForm.display_title) {
                  toast.error('Project and display title are required');
                  return;
                }
                setDemoConfigSaving(true);
                try {
                  const res = await fetch(`${API_URL}/api/admin/demo-projects`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                    body: JSON.stringify(demoConfigForm),
                  });
                  if (!res.ok) throw new Error('Failed to save');
                  toast.success('Demo project saved');
                  setDemoConfigDialogOpen(false);
                  // Refresh list
                  const updated = await fetch(`${API_URL}/api/admin/demo-projects`, { headers: { 'Authorization': `Bearer ${token}` } }).then(r => r.json());
                  setDemoProjects(updated || []);
                } catch (e) {
                  toast.error('Failed to save demo project');
                } finally {
                  setDemoConfigSaving(false);
                }
              }}
              disabled={demoConfigSaving || !demoConfigForm.project_id}
              className="bg-[#5AA2AE] hover:bg-[#5AA2AE]/90"
              data-testid="demo-config-save-btn"
            >
              {demoConfigSaving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
const QuestionLibraryDialog = memo(function QuestionLibraryDialog({
  open,
  onOpenChange,
  editingQuestion,
  questionLibraryContext,
  libraryDrivers,
  onSave,
  saving
}) {
  const defaultSliderConfig = {
    left_label: 'Low',
    middle_label: 'Medium',
    right_label: 'High',
    min_value: 0,
    max_value: 10,
    left_color: '',
    middle_color: '',
    right_color: '',
  };

  const [formData, setFormData] = useState({
    text: '',
    question_type: 'free_text',
    heading: '',
    required: false,
    allow_skip: false,
    options: [''],
    slider_config: defaultSliderConfig,
    driver_id: '',
    quick_start: false,
  });
  const [showSliderColors, setShowSliderColors] = useState(false);

  // Reset form when dialog opens/closes or editing question changes
  useEffect(() => {
    if (open) {
      if (editingQuestion) {
        setFormData({
          text: editingQuestion.text,
          question_type: editingQuestion.question_type,
          heading: editingQuestion.heading || '',
          required: editingQuestion.required || false,
          allow_skip: editingQuestion.allow_skip || false,
          options: editingQuestion.options?.length > 0 ? editingQuestion.options : [''],
          slider_config: editingQuestion.slider_config || defaultSliderConfig,
          driver_id: editingQuestion.driver_id || '',
          quick_start: editingQuestion.quick_start || false,
        });
        setShowSliderColors(!!(editingQuestion.slider_config?.left_color || editingQuestion.slider_config?.middle_color || editingQuestion.slider_config?.right_color));
      } else {
        setFormData({
          text: '',
          question_type: 'free_text',
          heading: '',
          required: false,
          allow_skip: false,
          options: [''],
          slider_config: defaultSliderConfig,
          driver_id: '',
          quick_start: false,
        });
        setShowSliderColors(false);
      }
    }
  }, [open, editingQuestion]);

  const handleSave = () => {
    onSave(formData, editingQuestion);
  };

  const updateOption = (index, value) => {
    const newOptions = [...formData.options];
    newOptions[index] = value;
    setFormData(prev => ({ ...prev, options: newOptions }));
  };

  const removeOption = (index) => {
    if (formData.options.length <= 1) return;
    setFormData(prev => ({ ...prev, options: prev.options.filter((_, i) => i !== index) }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>{editingQuestion ? 'Edit Question' : 'Add Question to Library'}</DialogTitle>
          <DialogDescription>
            {editingQuestion ? 'Update this question' : `Add a new question to the ${questionLibraryContext === 'about_me' ? 'About Me' : 'About Others'} library`}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-6 py-4 overflow-y-auto flex-1">
          {/* Driver (only for About Me context) */}
          {questionLibraryContext === 'about_me' && (
            <div className="space-y-2">
              <Label>Driver</Label>
              <Select
                value={formData.driver_id || 'none'}
                onValueChange={(value) => setFormData(prev => ({ ...prev, driver_id: value === 'none' ? '' : value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a driver" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Driver</SelectItem>
                  {libraryDrivers.map((driver) => (
                    <SelectItem key={driver.id} value={driver.id}>{driver.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500">Associate this question with a driver from the library</p>
            </div>
          )}

          {/* Heading */}
          <div className="space-y-2">
            <Label htmlFor="dialog-question-heading">Heading *</Label>
            <Input
              id="dialog-question-heading"
              placeholder="e.g., Communication"
              value={formData.heading}
              onChange={(e) => setFormData(prev => ({ ...prev, heading: e.target.value }))}
              required
            />
            <p className="text-xs text-slate-500">Required. Used to group questions on dashboards.</p>
          </div>

          {/* Question Text */}
          <div className="space-y-2">
            <Label htmlFor="dialog-question-text">Question Text *</Label>
            <Textarea
              id="dialog-question-text"
              placeholder="e.g., How satisfied are you with the communication from leadership?"
              value={formData.text}
              onChange={(e) => setFormData(prev => ({ ...prev, text: e.target.value }))}
              rows={3}
              required
            />
          </div>

          {/* Question Type */}
          <div className="space-y-2">
            <Label>Question Type</Label>
            <Select
              value={formData.question_type}
              onValueChange={(value) => setFormData(prev => ({ ...prev, question_type: value }))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="free_text">Free Text</SelectItem>
                <SelectItem value="multiple_choice">Multiple Choice</SelectItem>
                <SelectItem value="dropdown">Dropdown</SelectItem>
                <SelectItem value="slider">Slider</SelectItem>
                <SelectItem value="yes_no">Yes/No</SelectItem>
                <SelectItem value="checkbox">Checkbox (Multiple)</SelectItem>
                <SelectItem value="multi_topic">Multi-Topic (Ranked List)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Options for select types */}
          {['multiple_choice', 'dropdown', 'checkbox'].includes(formData.question_type) && (
            <div className="space-y-2">
              <Label>Options</Label>
              <div className="space-y-2">
                {formData.options.map((opt, i) => (
                  <div key={i} className="flex gap-2">
                    <Input
                      value={opt}
                      onChange={(e) => updateOption(i, e.target.value)}
                      placeholder={`Option ${i + 1}`}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeOption(i)}
                      disabled={formData.options.length <= 1}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setFormData(prev => ({ ...prev, options: [...prev.options, ''] }))}
                >
                  <Plus className="h-4 w-4 mr-1" /> Add Option
                </Button>
              </div>
            </div>
          )}

          {/* Slider config */}
          {['slider'].includes(formData.question_type) && (
            <div className="space-y-3 p-3 bg-slate-50 rounded-lg">
              <Label className="text-sm font-medium">Slider Labels</Label>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <Label className="text-xs text-slate-500">Left (1)</Label>
                  <Input
                    value={formData.slider_config.left_label}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      slider_config: { ...prev.slider_config, left_label: e.target.value }
                    }))}
                    placeholder="Not at all"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Middle (5)</Label>
                  <Input
                    value={formData.slider_config.middle_label}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      slider_config: { ...prev.slider_config, middle_label: e.target.value }
                    }))}
                    placeholder="(optional)"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Right (10)</Label>
                  <Input
                    value={formData.slider_config.right_label}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      slider_config: { ...prev.slider_config, right_label: e.target.value }
                    }))}
                    placeholder="Extremely"
                  />
                </div>
              </div>
              
              {/* Collapsible color pickers */}
              <div className="pt-2 border-t border-slate-200">
                <button
                  type="button"
                  onClick={() => setShowSliderColors(!showSliderColors)}
                  className="text-xs text-[#5AA2AE] hover:underline flex items-center gap-1"
                >
                  {showSliderColors ? '− Hide Custom Colors' : '+ Add Custom Colors'}
                </button>
                
                {showSliderColors && (
                  <div className="grid grid-cols-3 gap-2 mt-2">
                    <div>
                      <Label className="text-xs text-slate-500">Left Color</Label>
                      <Input
                        type="color"
                        value={formData.slider_config.left_color || '#ef4444'}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          slider_config: { ...prev.slider_config, left_color: e.target.value }
                        }))}
                        className="h-8 p-1 cursor-pointer"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Middle Color</Label>
                      <Input
                        type="color"
                        value={formData.slider_config.middle_color || '#eab308'}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          slider_config: { ...prev.slider_config, middle_color: e.target.value }
                        }))}
                        className="h-8 p-1 cursor-pointer"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Right Color</Label>
                      <Input
                        type="color"
                        value={formData.slider_config.right_color || '#22c55e'}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          slider_config: { ...prev.slider_config, right_color: e.target.value }
                        }))}
                        className="h-8 p-1 cursor-pointer"
                      />
                    </div>
                  </div>
                )}
              </div>
              
              {/* Number Scale */}
              <div className="grid grid-cols-2 gap-3 pt-2 border-t border-slate-200">
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">Min Value</Label>
                  <Input
                    type="number"
                    value={formData.slider_config.min_value ?? 0}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      slider_config: { ...prev.slider_config, min_value: parseInt(e.target.value) || 0 }
                    }))}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">Max Value</Label>
                  <Input
                    type="number"
                    value={formData.slider_config.max_value ?? 10}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      slider_config: { ...prev.slider_config, max_value: parseInt(e.target.value) || 10 }
                    }))}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Required */}
          <div className="flex items-center justify-between">
            <div>
              <Label htmlFor="dialog-lib-required">Required?</Label>
              <p className="text-xs text-slate-500">Users must answer this question</p>
            </div>
            <Switch
              id="dialog-lib-required"
              checked={formData.required}
              onCheckedChange={(checked) => setFormData(prev => ({ ...prev, required: checked }))}
            />
          </div>

          {/* Allow Skip */}
          <div className="flex items-center justify-between">
            <div>
              <Label htmlFor="dialog-lib-allow-skip">Allow Skip?</Label>
              <p className="text-xs text-slate-500">Show a "Skip" option with reason selection</p>
            </div>
            <Switch
              id="dialog-lib-allow-skip"
              checked={formData.allow_skip}
              onCheckedChange={(checked) => setFormData(prev => ({ ...prev, allow_skip: checked }))}
            />
          </div>

          {/* Quick Start */}
          <div className="flex items-center justify-between pt-4 border-t">
            <div>
              <Label htmlFor="dialog-lib-quick-start" className="flex items-center gap-2">
                <Rocket className="h-4 w-4 text-[#5AA2AE]" />
                Quick Start Question
              </Label>
              <p className="text-xs text-slate-500">Include in the "Quick Start" auto-setup for new projects</p>
            </div>
            <Switch
              id="dialog-lib-quick-start"
              checked={formData.quick_start}
              onCheckedChange={(checked) => setFormData(prev => ({ ...prev, quick_start: checked }))}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button 
            onClick={handleSave}
            disabled={saving || !formData.text.trim() || !formData.heading.trim()}
            className="bg-[#5AA2AE] hover:bg-[#5AA2AE]/90"
          >
            {saving ? 'Saving...' : (editingQuestion ? 'Update Question' : 'Add Question')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
});


function AdminInvoicesTab() {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const token = localStorage.getItem('pulse_token');

  React.useEffect(() => {
    const fetchInvoices = async () => {
      try {
        const res = await fetch(`${API_URL}/api/admin/invoices`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error();
        setData(await res.json());
      } catch {
        toast.error('Failed to load invoices');
      } finally {
        setLoading(false);
      }
    };
    fetchInvoices();
  }, [token]);

  if (loading) return <div className="text-center py-8 text-slate-400">Loading invoices...</div>;

  const transactions = data?.transactions || [];
  const invoices = data?.invoices || [];
  const allItems = [
    ...transactions.map(t => ({ ...t, type: 'payment', date: t.created_at })),
    ...invoices.map(i => ({ ...i, type: 'invoice', date: i.created_at })),
  ].sort((a, b) => new Date(b.date) - new Date(a.date));

  return (
    <Card data-testid="admin-invoices-tab">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CreditCard className="h-5 w-5 text-[#5AA2AE]" />
          All Invoices & Payments
        </CardTitle>
        <CardDescription>{allItems.length} total records across all organizations</CardDescription>
      </CardHeader>
      <CardContent>
        {allItems.length === 0 ? (
          <p className="text-center py-8 text-slate-400">No invoices or payments recorded yet.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Organization</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Package</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {allItems.map((item, i) => (
                <TableRow key={i}>
                  <TableCell className="text-sm">{new Date(item.date).toLocaleDateString()}</TableCell>
                  <TableCell className="text-sm font-medium">{item.organization_name || '—'}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="text-xs">
                      {item.type === 'payment' ? 'Stripe' : 'Invoice'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm">{item.package_id || item.description || '—'}</TableCell>
                  <TableCell className="text-sm font-semibold">${item.amount?.toLocaleString() || '—'}</TableCell>
                  <TableCell>
                    <Badge
                      className={
                        (item.payment_status === 'paid' || item.status === 'paid')
                          ? 'bg-green-100 text-green-700'
                          : 'bg-amber-100 text-amber-700'
                      }
                    >
                      {item.payment_status || item.status || 'pending'}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
